'use client';

import React, { useEffect, useLayoutEffect, useMemo, useState, useCallback, useRef } from 'react';
import dynamic from 'next/dynamic';
import type { ProductAttribute } from '@config3d/contracts';
import type { MaterialOverrides, AnimationControls } from '@config3d/scene';
import {
  evaluateMaterialGraph,
  stampSkipToneMapFromLibraryName,
  materialGraphUsesPerMeshUvRandomize,
  buildEffectiveVisibleMap,
  collectGroupMaterialReceiverIds,
  applyPlayerTextureQualityPreset,
  ensurePlayerTextureQuality,
  prefetchSceneModelAssetUrls,
  collectMaterialGraphImageUrls,
  warmMaterialGraphTextureCache,
  warmMaterialGraphEvalVariants,
  resolveTextAttrMaxCharsFromMaterials,
  truncateGraphemes,
  armMaterialTransitionClock,
  commitLayoutMaterialTransitionClock,
  getMaterialTransitionStartedAt,
  isSplatAssetFormat,
  isSplatAssetUrl,
  type SceneStats,
  type ViewerPostFX,
  type ViewerCanvasHandle,
} from '@config3d/scene';
import { useConfigurator, type SceneObjectData } from '../lib/use-configurator';
import { buildRuleCompPrefetchPlan } from '../lib/precompute-rule-comps';
import { firstWaveGlbUrls } from '../lib/first-wave-assets';
import { parsePlayerWarmupMode, PLAYER_WARMUP_DELAYS } from '../lib/player-warmup';
import { PerfOverlay } from './perf-overlay';
import { PlayerDebugPanel, type PlayerDebugDprMode, type PlayerDebugTexMode } from './player-debug-panel';
import { ComparisonMode } from './comparison-mode';
import { DimensionOverlay } from './dimension-overlay';
import { ThemeProvider } from './theme-provider';
import { ProductTour } from './product-tour';
import { HotspotOverlay } from './hotspot-overlay';
import { LoadingScreen } from './loading-screen';
import { BootLoader } from './boot-loader';
import { SocialMeta } from './social-meta';
import { PasswordGate } from './password-gate';
import { ShortcutHelp } from './shortcut-help';
import { MeasurementTool } from './measurement-tool';
import { ConfigOptionsPanel } from './config-options-panel';
import {
  ConfiguratorModeIntro,
  persistExperienceMode,
  readStoredExperienceMode,
  type ConfiguratorExperienceMode,
} from './configurator-mode-intro';
import { ConfigSummaryPanel } from './config-summary-panel';
import { t, setLocale, detectLocale, getSupportedLocales, getLocale } from '../lib/i18n';
import { getEmbedBridge, type EmbedBridge } from '../lib/embed-bridge';
import { getPlayerApiBase } from '../lib/api-base';
import type { AssetLoadStats } from '../lib/diagnostics-types';
import * as THREE from 'three';

// Patch Three.js ShaderChunk so bump map stacks on top of normal map
// instead of being mutually exclusive (#elif → separate #ifdef).
(() => {
  const chunk = THREE.ShaderChunk as Record<string, string>;
  const key = 'normal_fragment_maps';
  if (!chunk[key]) return;
  const exclusive = '#elif defined( USE_BUMPMAP )';
  if (chunk[key].includes(exclusive)) {
    chunk[key] = chunk[key].replace(exclusive, '#endif\n#ifdef USE_BUMPMAP');
  }
})();

const MOBILE_CONFIG_COLLAPSED_PX = 54;
/** Approximate open panel body height for canvas clearance (matches max-h on rail). */
const MOBILE_PANEL_BODY_EST_PX = 400;

/** Show/hide rules may reference scene object id (editor) or legacy name — accept both. */
function sceneObjectMatchesRuleTarget(obj: { id: string; name: string }, targets: Set<string>): boolean {
  return targets.has(obj.id) || targets.has(obj.name);
}

type RuleVisibilitySceneObject = {
  id: string;
  name: string;
  parentId?: string | null;
  metadata?: { parentId?: string | null; tags?: string[] };
  tags?: string[];
};

/** Pure visibility test (must stay in sync with `isRuleVisible` below). */
function sceneObjectRuleVisible(
  obj: RuleVisibilitySceneObject,
  ruleHiddenObjectTargets: Set<string>,
  ruleHiddenTags: Set<string>,
  objectsById: Map<string, RuleVisibilitySceneObject> | null,
): boolean {
  const parentIdOf = (o: RuleVisibilitySceneObject) => o.parentId ?? o.metadata?.parentId ?? null;
  const tagSet = (o: RuleVisibilitySceneObject) => {
    const m = o.metadata?.tags ?? [];
    const t = o.tags ?? [];
    return new Set([...m, ...t]);
  };
  if (sceneObjectMatchesRuleTarget(obj, ruleHiddenObjectTargets)) return false;
  for (const tag of tagSet(obj)) {
    if (ruleHiddenTags.has(tag)) return false;
  }
  if (!objectsById) return true;
  const visited = new Set<string>();
  let pid: string | null | undefined = parentIdOf(obj);
  while (pid) {
    if (visited.has(pid)) break;
    visited.add(pid);
    const p = objectsById.get(pid);
    if (!p) break;
    if (sceneObjectMatchesRuleTarget(p, ruleHiddenObjectTargets)) return false;
    for (const tag of tagSet(p)) {
      if (ruleHiddenTags.has(tag)) return false;
    }
    pid = parentIdOf(p);
  }
  return true;
}

/** BFS all descendant scene object ids under `rootId` (outliner parentId links). */
function collectDescendantSceneObjectIds(
  objects: Array<{ id: string; parentId?: string | null; metadata?: { parentId?: string | null } }>,
  rootId: string,
): string[] {
  const parentIdOf = (o: (typeof objects)[number]) => o.parentId ?? o.metadata?.parentId ?? null;
  const byParent = new Map<string | null, string[]>();
  for (const o of objects) {
    const pid = parentIdOf(o);
    const list = byParent.get(pid) ?? [];
    list.push(o.id);
    byParent.set(pid, list);
  }
  const out: string[] = [];
  const queue = [...(byParent.get(rootId) ?? [])];
  const seen = new Set<string>();
  while (queue.length) {
    const id = queue.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    for (const c of byParent.get(id) ?? []) queue.push(c);
  }
  return out;
}

function detectMobileDevice(): { isMobile: boolean; isTablet: boolean; isLowPower: boolean } {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { isMobile: false, isTablet: false, isLowPower: false };
  }
  const ua = navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua) || (ua.includes('mac') && 'ontouchend' in document);
  const isAndroid = /android/.test(ua);
  const isTablet = isIOS
    ? /ipad/.test(ua) || (ua.includes('mac') && 'ontouchend' in document)
    : isAndroid && !/mobile/.test(ua);
  const isMobile = isIOS || isAndroid;
  const sw = typeof screen !== 'undefined' ? screen.width : 1920;
  const sh = typeof screen !== 'undefined' ? screen.height : 1080;
  const maxDim = Math.max(sw, sh) * (window.devicePixelRatio || 1);
  // iPad/iPhone report high CSS pixels but share a tight WebGL memory budget.
  // Treat all iOS as low-power so we don't run desktop keep-all + 8× MSAA.
  const isLowPower = isMobile && (isIOS || maxDim < 3200);
  return { isMobile, isTablet, isLowPower };
}

function sceneObjectModifiers(obj: { modifiers?: unknown; metadata?: unknown }): unknown[] | undefined {
  const top = (obj as { modifiers?: unknown }).modifiers;
  const nested = (obj.metadata as { modifiers?: unknown } | undefined)?.modifiers;
  const pick = Array.isArray(top) && top.length > 0 ? top : Array.isArray(nested) && nested.length > 0 ? nested : undefined;
  return pick as unknown[] | undefined;
}

function objectHasSceneTagForRules(obj: { metadata?: { tags?: string[] }; tags?: string[] }, tagId: string): boolean {
  const m = obj.metadata?.tags;
  const t = obj.tags;
  return !!(m?.includes(tagId) || t?.includes(tagId));
}

function modifierMatchesRuleKey(m: { id: string; name?: string; tags?: string[] }, key: string): boolean {
  const k = key.trim();
  if (!k) return false;
  if (m.id === k) return true;
  if (m.name != null && String(m.name).trim() === k) return true;
  if (m.tags?.includes(k)) return true;
  return false;
}

function expandModifierRuleObjectIds(
  targetId: string,
  objects: Array<{ id: string; metadata?: unknown; tags?: string[] }>,
): string[] {
  if (targetId.startsWith('tag:')) {
    const tid = targetId.slice(4);
    return objects
      .filter((o) => objectHasSceneTagForRules(o as { metadata?: { tags?: string[] }; tags?: string[] }, tid))
      .map((o) => o.id);
  }
  return [targetId];
}

/** Last matching enable/disable rule wins per object + modifier instance id. */
function buildModifierRuleEnableMap(
  objects: Array<{ id: string; metadata?: unknown; modifiers?: unknown }> | undefined,
  ruleActions: Array<{ type: string; targetId: string; value?: unknown }>,
): Map<string, Map<string, boolean>> {
  const out = new Map<string, Map<string, boolean>>();
  if (!objects?.length) return out;
  for (const a of ruleActions) {
    if (a.type !== 'enable-modifier' && a.type !== 'disable-modifier') continue;
    const modKey = String(a.value ?? '').trim();
    if (!modKey || !a.targetId) continue;
    const enable = a.type === 'enable-modifier';
    for (const oid of expandModifierRuleObjectIds(a.targetId, objects)) {
      const obj = objects.find((o) => o.id === oid);
      const mods = sceneObjectModifiers(obj as { modifiers?: unknown; metadata?: unknown }) as
        | Array<{ id: string; name?: string; tags?: string[] }>
        | undefined;
      if (!mods?.length) continue;
      for (const m of mods) {
        if (!modifierMatchesRuleKey(m, modKey)) continue;
        if (!out.has(oid)) out.set(oid, new Map());
        out.get(oid)!.set(m.id, enable);
      }
    }
  }
  return out;
}

type ModifierParamOverride = string | number | boolean;

/** Later `set-modifier-param` actions win per object + modifier instance id + param key. */
function buildModifierRuleParamMap(
  objects: Array<{ id: string; metadata?: unknown; modifiers?: unknown }> | undefined,
  ruleActions: Array<{ type: string; targetId: string; value?: unknown }>,
): Map<string, Map<string, Record<string, ModifierParamOverride>>> {
  const out = new Map<string, Map<string, Record<string, ModifierParamOverride>>>();
  if (!objects?.length) return out;
  for (const a of ruleActions) {
    if (a.type !== 'set-modifier-param') continue;
    if (!a.targetId) continue;
    const payload = a.value as { modifierKey?: string; paramKey?: string; paramValue?: ModifierParamOverride } | undefined;
    if (!payload || typeof payload !== 'object') continue;
    const modifierKey = String(payload.modifierKey ?? '').trim();
    const paramKey = String(payload.paramKey ?? '').trim();
    if (!modifierKey || !paramKey) continue;
    const paramValue = payload.paramValue as ModifierParamOverride;
    for (const oid of expandModifierRuleObjectIds(a.targetId, objects)) {
      const obj = objects.find((o) => o.id === oid);
      const mods = sceneObjectModifiers(obj as { modifiers?: unknown; metadata?: unknown }) as
        | Array<{ id: string; name?: string; tags?: string[] }>
        | undefined;
      if (!mods?.length) continue;
      for (const m of mods) {
        if (!modifierMatchesRuleKey(m, modifierKey)) continue;
        if (!out.has(oid)) out.set(oid, new Map());
        const perObj = out.get(oid)!;
        if (!perObj.has(m.id)) perObj.set(m.id, {});
        perObj.get(m.id)![paramKey] = paramValue;
      }
    }
  }
  return out;
}

function mergeModifiersWithRuleMaps(
  obj: { id: string; metadata?: unknown; modifiers?: unknown },
  enableMap: Map<string, Map<string, boolean>>,
  paramMap: Map<string, Map<string, Record<string, ModifierParamOverride>>>,
  opts?: { mobileArrayCap?: number },
): unknown[] | undefined {
  const raw = sceneObjectModifiers(obj) as Array<Record<string, unknown>> | undefined;
  if (!raw?.length) return raw;
  const perEnable = enableMap.get(obj.id);
  const perParam = paramMap.get(obj.id);
  const arrayCap = opts?.mobileArrayCap;
  if ((!perEnable || perEnable.size === 0) && (!perParam || perParam.size === 0) && !arrayCap) return raw;
  return raw.map((m) => {
    const id = m.id as string;
    let next: Record<string, unknown> = { ...m };
    if (perEnable?.has(id)) next = { ...next, enabled: perEnable.get(id)! };
    const pOverrides = perParam?.get(id);
    const prevParams =
      m.params && typeof m.params === 'object' && !Array.isArray(m.params)
        ? { ...(m.params as Record<string, unknown>) }
        : {};
    let params = pOverrides && Object.keys(pOverrides).length > 0 ? { ...prevParams, ...pOverrides } : prevParams;
    if (
      arrayCap &&
      typeof arrayCap === 'number' &&
      arrayCap > 0 &&
      (m.type === 'arrayRadial' || m.type === 'arrayLinear' || m.type === 'array') &&
      next.enabled !== false
    ) {
      const count = Number(params.count);
      if (Number.isFinite(count) && count > arrayCap) {
        params = { ...params, count: arrayCap };
      }
    }
    if (params !== prevParams || pOverrides) next = { ...next, params };
    return next;
  });
}

class ModelErrorBoundary extends React.Component<
  { children: React.ReactNode; resetKey?: string },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; resetKey?: string }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidUpdate(prevProps: { resetKey?: string }) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

/** Ad blockers often block `/api/analytics/*`; API also serves the same routes under `/api/c3d-stats`. */
function statsEventsUrl(): string {
  return `${getPlayerApiBase()}/api/c3d-stats/events`;
}

const CAMERA_PRESETS: { label: string; key: string; position: [number, number, number] }[] = [
  { label: 'Front', key: 'front', position: [0, 1, 3] },
  { label: 'Side', key: 'side', position: [3, 1, 0] },
  { label: 'Top', key: 'top', position: [0, 4, 0.01] },
  { label: '3/4', key: 'perspective', position: [2, 1.5, 2] },
];

type AutoRotateMode = 'off' | 'rotate' | 'rotate-reset';

function HelpDialog({
  onClose,
  modelCount,
  loadStartTime,
  allLoaded,
  sceneStats,
  assetStats,
}: {
  onClose: () => void;
  modelCount: number;
  loadStartTime: number;
  allLoaded: boolean;
  sceneStats?: SceneStats | null;
  assetStats?: AssetLoadStats | null;
}) {
  const [showStats, setShowStats] = useState(false);
  const [perf, setPerf] = useState({ fps: 0, frameTime: 0, memory: null as number | null });
  const rafRef = useRef<number>(0);
  const framesRef = useRef<number[]>([]);

  useEffect(() => {
    if (!showStats) return;
    let lastTime = performance.now();
    function tick() {
      const now = performance.now();
      const delta = now - lastTime;
      lastTime = now;
      framesRef.current.push(delta);
      if (framesRef.current.length > 60) framesRef.current.shift();
      const avgFrame = framesRef.current.reduce((a, b) => a + b, 0) / framesRef.current.length;
      const mem = (performance as any).memory
        ? Math.round((performance as any).memory.usedJSHeapSize / 1048576)
        : null;
      setPerf({
        fps: Math.round(1000 / avgFrame),
        frameTime: Math.round(avgFrame * 10) / 10,
        memory: mem,
      });
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [showStats]);

  const loadTime = allLoaded ? Math.round(Date.now() - loadStartTime) : null;
  const fpsColor = perf.fps >= 55 ? 'text-green-600 dark:text-green-400' : perf.fps >= 30 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-xs bg-white dark:bg-zinc-900 rounded-xl shadow-2xl border border-zinc-200 dark:border-zinc-800 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-sm font-semibold mb-3">Keyboard Shortcuts</h3>
        <div className="space-y-1.5 text-sm">
          {[
            ['←  →', 'Cycle component variants'],
            ['1–4', 'Camera presets'],
            ['F', 'Toggle fullscreen'],
            ['R', 'Reset configuration'],
            ['Shift+R', 'Cycle auto-rotate'],
            ['Esc', 'Close dialogs'],
            ['?', 'Show/hide this panel'],
          ].map(([key, desc]) => (
            <div key={key} className="flex items-center justify-between">
              <kbd className="text-[11px] font-mono bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 px-1.5 py-0.5 rounded">{key}</kbd>
              <span className="text-xs text-zinc-500">{desc}</span>
            </div>
          ))}
        </div>

        <div className="mt-4 border-t border-zinc-200 dark:border-zinc-700 pt-3">
          <button
            onClick={() => setShowStats((v) => !v)}
            className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors w-full"
          >
            <svg className={`h-3 w-3 transition-transform ${showStats ? 'rotate-90' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
            Statistics
          </button>
          {showStats && (
            <div className="mt-2 space-y-1 font-mono text-[11px]">
              <div className="flex justify-between">
                <span className="text-zinc-400">FPS</span>
                <span className={fpsColor}>{perf.fps}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Frame</span>
                <span className="text-zinc-600 dark:text-zinc-300">{perf.frameTime}ms</span>
              </div>
              {perf.memory !== null && (
                <div className="flex justify-between">
                  <span className="text-zinc-400">Memory</span>
                  <span className="text-zinc-600 dark:text-zinc-300">{perf.memory}MB</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-zinc-400">Models</span>
                <span className="text-zinc-600 dark:text-zinc-300">{modelCount}</span>
              </div>
              {assetStats && (
                <>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wide pt-1">Assets / scene</div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Product</span>
                    <span className="text-zinc-600 dark:text-zinc-300 truncate max-w-[140px]" title={assetStats.productId}>{assetStats.productId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Scene objects</span>
                    <span className="text-zinc-600 dark:text-zinc-300">{assetStats.sceneObjectCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Models (viewer)</span>
                    <span className="text-zinc-600 dark:text-zinc-300">{assetStats.sceneModelsRendered}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">GLB in catalog</span>
                    <span className="text-zinc-600 dark:text-zinc-300">{assetStats.catalogGlbCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Library materials</span>
                    <span className="text-zinc-600 dark:text-zinc-300">{assetStats.libraryMaterialCount}</span>
                  </div>
                </>
              )}
              {sceneStats && (
                <>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wide pt-1">GPU / mesh (live)</div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400" title="Per-mesh geometry × InstancedMesh.count (e.g. radial array)">Triangles (total)</span>
                    <span className="text-zinc-600 dark:text-zinc-300">{sceneStats.triangles.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400" title="WebGLRenderer.info; may match total when post-processing under-reports">Triangles (GPU info)</span>
                    <span className="text-zinc-600 dark:text-zinc-300">{sceneStats.renderTriangles.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Vertices</span>
                    <span className="text-zinc-600 dark:text-zinc-300">{sceneStats.vertices.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Meshes</span>
                    <span className="text-zinc-600 dark:text-zinc-300">{sceneStats.meshes}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Instanced</span>
                    <span className="text-zinc-600 dark:text-zinc-300">{sceneStats.instancedMeshes}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Skinned</span>
                    <span className="text-zinc-600 dark:text-zinc-300">{sceneStats.skinnedMeshes}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Materials</span>
                    <span className="text-zinc-600 dark:text-zinc-300">{sceneStats.materials}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Draw calls</span>
                    <span className="text-zinc-600 dark:text-zinc-300">{sceneStats.drawCalls}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Lines / points</span>
                    <span className="text-zinc-600 dark:text-zinc-300">{sceneStats.renderLines} / {sceneStats.renderPoints}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Textures</span>
                    <span className="text-zinc-600 dark:text-zinc-300">{sceneStats.textures}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Geometries</span>
                    <span className="text-zinc-600 dark:text-zinc-300">{sceneStats.geometries}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Shader programs</span>
                    <span className="text-zinc-600 dark:text-zinc-300">{sceneStats.programs}</span>
                  </div>
                </>
              )}
              {loadTime !== null && (
                <div className="flex justify-between">
                  <span className="text-zinc-400">Load</span>
                  <span className="text-zinc-600 dark:text-zinc-300">{loadTime > 1000 ? `${(loadTime / 1000).toFixed(1)}s` : `${loadTime}ms`}</span>
                </div>
              )}
              <div className="pt-1">
                <div className="w-full bg-zinc-200 dark:bg-zinc-700 rounded-full h-1">
                  <div
                    className={`h-1 rounded-full transition-all ${perf.fps >= 55 ? 'bg-green-500' : perf.fps >= 30 ? 'bg-yellow-500' : 'bg-red-500'}`}
                    style={{ width: `${Math.min(100, (perf.fps / 60) * 100)}%` }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          className="mt-4 w-full text-center text-xs text-zinc-400 hover:text-zinc-600 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
}

function AutoRotateToolbarButton({
  autoRotateMode,
  onAutoRotateCycle,
  className = '',
  darkGlass = false,
  /** First segment inside the rounded viewer toolbar — no extra rounding/shadow (avoids overlapping the bar). */
  toolbarStrip = false,
}: {
  autoRotateMode: AutoRotateMode;
  onAutoRotateCycle: () => void;
  className?: string;
  /** Match the dark glass cluster on the canvas (right toolbar / embed). */
  darkGlass?: boolean;
  toolbarStrip?: boolean;
}) {
  const strip = toolbarStrip;
  const shape = strip ? 'rounded-none shadow-none backdrop-blur-none' : 'rounded-full backdrop-blur-md';
  /** Strip uses the same `w-px` dividers as other tools — no segment border-r (avoids double lines). */
  const stripBorder = strip ? 'border-0' : 'border';

  return (
    <button
      type="button"
      onClick={onAutoRotateCycle}
      className={`relative h-8 w-8 shrink-0 flex items-center justify-center transition-colors duration-200 ${shape} ${stripBorder} ${
        darkGlass
          ? autoRotateMode !== 'off'
            ? strip
              ? 'bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900'
              : 'bg-zinc-900 text-white border-zinc-800 shadow-md shadow-black/15 dark:bg-zinc-100 dark:text-zinc-900 dark:border-zinc-300'
            : strip
              ? 'bg-transparent text-zinc-700 hover:bg-zinc-900/[0.06] hover:text-zinc-900 dark:text-zinc-200 dark:hover:bg-white/10 dark:hover:text-white'
              : 'bg-white/95 text-zinc-800 border-zinc-900/15 shadow-sm hover:bg-white hover:text-zinc-950 dark:bg-zinc-800/95 dark:text-zinc-100 dark:border-white/15 dark:hover:bg-zinc-800'
          : autoRotateMode !== 'off'
            ? 'bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 border-zinc-800 dark:border-zinc-200 shadow-sm'
            : 'bg-white/90 dark:bg-zinc-800/90 border border-zinc-200/80 dark:border-zinc-600 text-zinc-500 dark:text-zinc-400 hover:text-zinc-800 dark:hover:text-zinc-200'
      } ${className}`}
      title={autoRotateMode === 'off' ? 'Auto-rotate: Off' : autoRotateMode === 'rotate' ? 'Auto-rotate: On' : 'Auto-rotate: On + Reset'}
    >
      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182M20.015 4.65v4.992" />
      </svg>
      {autoRotateMode === 'rotate-reset' && (
        <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-zinc-500 border border-white dark:border-zinc-200" />
      )}
    </button>
  );
}

function useDarkMode() {
  const [dark, setDark] = useState(false);
  useEffect(() => {
    const stored = localStorage.getItem('player-theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const isDark = stored === 'dark' || (!stored && prefersDark);
    setDark(isDark);
    document.documentElement.classList.toggle('dark', isDark);
  }, []);
  function toggle() {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('player-theme', next ? 'dark' : 'light');
  }
  return { dark, toggle };
}

const ViewerCanvas = dynamic(
  () => import('@config3d/scene').then((mod) => ({ default: mod.ViewerCanvas })),
  { ssr: false },
);

const GlbModel = dynamic(
  () => import('@config3d/scene').then((mod) => ({ default: mod.GlbModel })),
  { ssr: false },
);

const ModifiedMesh = dynamic(
  () => import('@config3d/scene').then((mod) => ({ default: mod.ModifiedMesh })),
  { ssr: false },
);
const PrimitiveMaterial = dynamic(
  () => import('@config3d/scene').then((mod) => ({ default: mod.PrimitiveMaterial })),
  { ssr: false },
);

const Hotspot = dynamic(
  () => import('@config3d/scene').then((mod) => ({ default: mod.Hotspot })),
  { ssr: false },
);

const ObjectRotationGroup = dynamic(
  () => import('@config3d/scene').then((mod) => ({ default: mod.ObjectRotationGroup })),
  { ssr: false },
);

const GlobalSplatProvider = dynamic(
  () => import('@config3d/scene').then((mod) => ({ default: mod.GlobalSplatProvider })),
  { ssr: false },
);

const GsplatObject = dynamic(
  () => import('@config3d/scene').then((mod) => ({ default: mod.GsplatObject })),
  { ssr: false },
);

const SplatCutoutObject = dynamic(
  () => import('@config3d/scene').then((mod) => ({ default: mod.SplatCutoutObject })),
  { ssr: false },
);

interface Props {
  productId: string;
  initialSelections?: Record<string, unknown>;
  embed?: boolean;
  hideControls?: boolean;
  /**
   * URL `?hideConfigurator=true` — hide the built-in configurator panel (config
   * rail + summary + intro) while keeping the 3D viewer and toolbar. Intended
   * for host pages that render their own configurator UI and drive the model
   * via the embed SDK (`setAttribute` / `selectComponent`). Alias of
   * `hideControls` with an integration-friendly name.
   */
  hideConfigurator?: boolean;
  /** URL `?hideToolbar=true` — hide only the top-right viewer strip (auto-rotate, help, reload, screenshot, compare, fullscreen). */
  hideToolbar?: boolean;
  /** URL `?hideUi=true` — hide all chrome: config rail, toolbars, headers, camera presets, animation bar, validation toasts, embed controls, tours, measurement/hotspot overlays. Canvas plus loading screen (password gate + critical errors still show). */
  hideUi?: boolean;
  /**
   * URL `?cleanView=true` (or product metadata `playerChromeMinimal`) — hide top product bar,
   * viewer toolbar, config “Configuration & export” footer, and dimension axis legend.
   * Keeps the right-side configuration options rail.
   */
  cleanView?: boolean;
  /**
   * URL `?tex=` — caps/scales textures in the player (see `applyPlayerTextureQualityPreset`).
   */
  textureQualityPreset?: string | null;
  /** URL `?perf=low` — force mobile-class performance budget (lower DPR, transmission, textures). */
  perfPreset?: string | null;
  /** URL `?debug=1` — open the scene debug overlay (AA / DPR / postfx) without saving playerDebug on the product. */
  debugOverlay?: boolean;
}

function selectionsToMaterialOverrides(
  selections: Record<string, unknown>,
  attributes: ProductAttribute[],
): MaterialOverrides {
  const overrides: MaterialOverrides = {};

  for (const attr of attributes) {
    const val = selections[attr.key];
    if (val == null) continue;
    const strVal = String(val);

    const attrValue = attr.values?.find((v) => v.key === strVal);

    // Global scene tint: only true color attributes (or key `color`).
    // Do NOT apply `key === 'material'` (or similar) here — those selections
    // drive material *graphs* via evaluateMaterialGraph; applying presets to
    // every mesh made unrelated parts of the model change when no graph was
    // assigned to them.
    if (attr.type === 'color' || attr.key === 'color') {
      const resolved = attrValue?.color;
      if (resolved) {
        overrides.color = resolved;
      } else if (strVal.startsWith('#')) {
        overrides.color = strVal;
      }
      const meta = attrValue?.metadata as Record<string, unknown> | undefined;
      if (meta) {
        if (typeof meta.roughness === 'number') overrides.roughness = meta.roughness;
        if (typeof meta.metalness === 'number') overrides.metalness = meta.metalness;
        if (typeof meta.clearcoat === 'number') overrides.clearcoat = meta.clearcoat;
      }
    }
  }

  return overrides;
}

const EMPTY_SELECTIONS: Record<string, unknown> = {};

function urlWantsDebugOverlay(): boolean {
  if (typeof window === 'undefined') return false;
  return /(?:\?|&)\??(?:debug|playerDebug)(?:=1|=true|=yes|=on)?(?:&|$)/i.test(window.location.search);
}

function viewerPostfxAllOff(base: ViewerPostFX): ViewerPostFX {
  return {
    ...base,
    enabled: false,
    antialiasing: 'none',
    dof: base.dof ? { ...base.dof, enabled: false } : undefined,
    bloom: base.bloom ? { ...base.bloom, enabled: false } : undefined,
    vignette: base.vignette ? { ...base.vignette, enabled: false } : undefined,
    chromaticAberration: base.chromaticAberration ? { ...base.chromaticAberration, enabled: false } : undefined,
    ssao: base.ssao ? { ...base.ssao, enabled: false } : undefined,
    sharpenBlur: base.sharpenBlur ? { ...base.sharpenBlur, enabled: false } : undefined,
  };
}

export function ConfiguratorPlayer({
  productId,
  initialSelections,
  embed,
  hideControls,
  hideConfigurator = false,
  hideToolbar = false,
  hideUi = false,
  cleanView = false,
  textureQualityPreset,
  perfPreset,
  debugOverlay = false,
}: Props) {
  const noViewerChrome = !!hideUi;
  const effectiveHideControls = hideControls || hideConfigurator || noViewerChrome;

  const { dark, toggle: toggleDark } = useDarkMode();
  const {
    sessionId, productModel, configState, assets, componentVariants, sceneData, libraryMaterials, getRuleGraphs,
    loading, error,
    initSession, setAttribute, selectComponent, resetConfiguration,
  } = useConfigurator(productId, initialSelections);

  const effectiveChromeMinimal = useMemo(() => {
    if (hideUi) return false;
    if (cleanView) return true;
    return !!(productModel?.metadata as { playerChromeMinimal?: boolean } | undefined)?.playerChromeMinimal;
  }, [hideUi, cleanView, productModel?.metadata]);

  const effectiveHideToolbar = hideToolbar || noViewerChrome || effectiveChromeMinimal;
  const effectiveHideHeader = effectiveChromeMinimal;
  const showProductHeader = !embed && !effectiveHideControls && !effectiveHideHeader;
  const configRailTopClass = showProductHeader ? 'lg:top-14' : 'lg:top-4';

  const [locale, setLocaleState] = useState('en');
  const [accessGranted, setAccessGranted] = useState(false);
  const [accessLevel, setAccessLevel] = useState<string>('public');
  /** Declared early: material warm path + keep-mounted swap path both depend on it. */
  const [firstRevealReady, setFirstRevealReady] = useState(false);
  /** Keep visible-object graph override object identity when firstRevealReady only expands hidden meshes. */
  const frozenVisibleGraphRef = useRef<Record<string, Record<string, Record<string, any>> | null> | null>(null);
  const frozenVisibleGraphSelRef = useRef('');

  useEffect(() => {
    fetch(`${getPlayerApiBase()}/api/access/${productId}`)
      .then((r) => r.json())
      .then((data) => {
        setAccessLevel(data.level || 'public');
        if (data.level !== 'password') {
          setAccessGranted(true);
        } else if (sessionStorage.getItem(`c3d_access_${productId}`) === 'granted') {
          setAccessGranted(true);
        }
      })
      .catch(() => setAccessGranted(true));
  }, [productId]);

  useEffect(() => {
    const detected = detectLocale();
    setLocale(detected);
    setLocaleState(detected);
  }, []);

  const handleLocaleChange = useCallback((newLocale: string) => {
    setLocale(newLocale);
    setLocaleState(newLocale);
  }, []);

  useEffect(() => { initSession(); }, [initSession]);

  const playerDebugEnabled = !!(sceneData as any)?.metadata?.playerDebug || debugOverlay || urlWantsDebugOverlay();
  const productTourEnabled = !!(sceneData as any)?.metadata?.productTour;

  const texOpts = useMemo(() => {
    const raw = (sceneData as any)?.metadata?.texOpts;
    return {
      desktop: (raw?.desktop as string) || 'full',
      // Match scene / product defaults — Paradis ships mobile+desktop at 2k.
      mobile: (raw?.mobile as string) || '2k',
      tablet: (raw?.tablet as string) || (raw?.mobile as string) || '2k',
      webp: raw?.webp !== false,
    };
  }, [sceneData]);

  const deviceInfo = useMemo(() => {
    const detected = detectMobileDevice();
    if (perfPreset === 'low') return { isMobile: true, isTablet: true, isLowPower: true };
    return detected;
  }, [perfPreset]);

  /**
   * Shared cage shell used by wood / carpaint / straw / metal-mesh.
   * Kept mounted (hidden OK) so leather→wood does not remount + reload.
   * Also used as a visual bridge while exclusive finishes decode on swap.
   */
  const shellBridgeMountIds = useMemo(() => {
    if (!sceneData?.objects?.length) return null;
    const pinned = new Set<string>();
    const byId = new Map(sceneData.objects.map((o) => [o.id, o]));
    const seedIds = [
      'cmnfpqvo4006rnpmw08wxygsu', // Basic group
      'cmn8wy04w003hnprwaa15ff6m', // Basic
      'cmnfoo6vh005lnpmwv2o26eny', // Basic Cap
      'cmnka8ksk001vnpw0s8h3snno', // Engraving
      'cmrkfqcfo001vnpng79fq090i', // Decanter Metal - Cap Divider
    ];
    for (const id of seedIds) {
      if (!byId.has(id)) continue;
      pinned.add(id);
      for (const desc of collectDescendantSceneObjectIds(sceneData.objects, id)) pinned.add(desc);
    }
    return pinned;
  }, [sceneData?.objects]);

  const mobilePinnedMountIds = useMemo(() => {
    if (!deviceInfo.isMobile) return null;
    return shellBridgeMountIds;
  }, [deviceInfo.isMobile, shellBridgeMountIds]);

  /**
   * Leather / Rubber are exclusive finish trees (separate GLBs). Keeping them mounted while
   * rule-hidden (desktop keepAll) lets fast material swaps flash the wrong body for a frame
   * when visibility/material updates land out of lockstep. Always unmount when hidden.
   */
  const exclusiveFinishRoots = useMemo(
    () =>
      [
        'cmnh5taem000hnp6cm06gwqto', // Leather
        'cmnh5u1h5000jnp6c214h9t43', // Rubber
      ] as const,
    [],
  );

  /** Map exclusive finish root → material attribute key. */
  const exclusiveFinishMaterialByRoot = useMemo(
    () =>
      new Map<string, string>([
        ['cmnh5taem000hnp6cm06gwqto', 'leather'],
        ['cmnh5u1h5000jnp6c214h9t43', 'rubber'],
      ]),
    [],
  );

  const exclusiveFinishMountIds = useMemo(() => {
    if (!sceneData?.objects?.length) return null;
    const ids = new Set<string>();
    const byId = new Map(sceneData.objects.map((o) => [o.id, o]));
    for (const id of exclusiveFinishRoots) {
      if (!byId.has(id)) continue;
      ids.add(id);
      for (const desc of collectDescendantSceneObjectIds(sceneData.objects, id)) ids.add(desc);
    }
    return ids;
  }, [sceneData?.objects, exclusiveFinishRoots]);

  /** objectId → exclusive finish root id (Leather / Rubber). */
  const exclusiveFinishRootById = useMemo(() => {
    const map = new Map<string, string>();
    if (!sceneData?.objects?.length) return map;
    const byId = new Map(sceneData.objects.map((o) => [o.id, o]));
    for (const rootId of exclusiveFinishRoots) {
      if (!byId.has(rootId)) continue;
      map.set(rootId, rootId);
      for (const desc of collectDescendantSceneObjectIds(sceneData.objects, rootId)) {
        map.set(desc, rootId);
      }
    }
    return map;
  }, [sceneData?.objects, exclusiveFinishRoots]);

  /** Roots the user has fully revealed once — stay mounted while hidden afterward. */
  const [visitedExclusiveRoots, setVisitedExclusiveRoots] = useState<Set<string>>(() => new Set());
  const visitedExclusiveRootsRef = useRef(visitedExclusiveRoots);
  visitedExclusiveRootsRef.current = visitedExclusiveRoots;
  /**
   * After first paint, mount one exclusive finish at a time (hidden) to decode GLBs + maps
   * so the first user tap is not an empty wait. Cleared when that root is marked visited.
   */
  const [bgWarmExclusiveRoot, setBgWarmExclusiveRoot] = useState<string | null>(null);

  const [debugDprMode, setDebugDprMode] = useState<PlayerDebugDprMode>('default');
  const [debugShadowsOn, setDebugShadowsOn] = useState(true);
  const [debugPostfxOn, setDebugPostfxOn] = useState(true);
  const [debugTransmissionFull, setDebugTransmissionFull] = useState(true);
  const [debugTexMode, setDebugTexMode] = useState<PlayerDebugTexMode>('embed');
  const [debugLowPowerGl, setDebugLowPowerGl] = useState(false);

  // Apply during render (not useEffect): GlbModel's clonedScene depends on textureQualityEpoch.
  // Default module settings are "passive" until this runs; an effect fires after the first child
  // commit → epoch bumps → full scene reclone + detachEmbeddedTexturesForPlayer (canvas downscale
  // per map). On iPad that duplicate pass dominated load time (~minutes vs ~seconds).
  // Use silent:true so we never notify useSyncExternalStore mid-render (React setState error).
  if (typeof window !== 'undefined') {
    const silent = { silent: true as const };
    const explicit = textureQualityPreset ?? undefined;
    if (!playerDebugEnabled) {
      // URL/embed `textureQualityPreset` only when explicitly provided; otherwise scene texOpts.
      if (explicit) applyPlayerTextureQualityPreset(explicit, silent);
      else if (deviceInfo.isMobile) {
        applyPlayerTextureQualityPreset(
          deviceInfo.isTablet ? texOpts.tablet : texOpts.mobile,
          silent,
        );
      } else applyPlayerTextureQualityPreset(texOpts.desktop, silent);
      // CPU graph bakes (wood blend/HSL/UV) write the *final* maps. Keep them aligned with
      // the download preset — a 1k bake after a 2k download made iPad wood look soft vs desktop.
      // Phones stay at 512 (Safari OOM). Tablets match 2k/desktop bake (2048).
      ensurePlayerTextureQuality({
        webp: texOpts.webp,
        cpuBakeMaxEdge: deviceInfo.isMobile
          ? deviceInfo.isTablet
            ? 2048
            : 512
          : undefined,
      });
    } else {
      const p = debugTexMode === 'embed' ? explicit : debugTexMode;
      applyPlayerTextureQualityPreset(p, silent);
      ensurePlayerTextureQuality({
        webp: texOpts.webp,
        cpuBakeMaxEdge: deviceInfo.isMobile
          ? deviceInfo.isTablet
            ? 2048
            : 512
          : undefined,
      });
    }
  }

  const bridgeRef = React.useRef<EmbedBridge | null>(null);
  const configStateRef = React.useRef(configState);
  configStateRef.current = configState;
  const sessionIdRef = React.useRef(sessionId);
  sessionIdRef.current = sessionId;
  const viewerRef = React.useRef<ViewerCanvasHandle | null>(null);

  useEffect(() => {
    if (!embed) return;
    const bridge = getEmbedBridge();
    bridgeRef.current = bridge;
    bridge.setHandler({
      setAttribute,
      selectComponent,
      resetConfiguration,
      getState: () => (configStateRef.current
        ? { ...configStateRef.current, sessionId: sessionIdRef.current }
        : configStateRef.current),
      requestSnapshot: async (options) => {
        const viewer = viewerRef.current;
        if (!viewer) return null;
        return viewer.captureSnapshot(options);
      },
    });
    return () => { bridge.setHandler(null); bridgeRef.current = null; };
  }, [embed, setAttribute, selectComponent, resetConfiguration]);

  useEffect(() => {
    if (!embed || !bridgeRef.current || !productModel || !configState) return;
    bridgeRef.current.emit('c3d:player.ready', {
      productId,
      productName: productModel.name,
      sessionId,
      capabilities: { materialSwap: true, variantSwitch: true, snapshots: true, animation: true, arMode: true },
    });
  }, [embed, productModel, productId, configState, sessionId]);

  useEffect(() => {
    if (!embed || !bridgeRef.current || !configState) return;
    bridgeRef.current.emit('c3d:configuration.changed', {
      sessionId,
      selections: configState.selections,
      revision: configState.revision,
      status: configState.status,
    });
  }, [embed, configState, sessionId]);

  useEffect(() => {
    if (!productId) return;
    const visitorId = (() => {
      try {
        let id = localStorage.getItem('c3d_vid');
        if (!id) { id = `v_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem('c3d_vid', id); }
        return id;
      } catch { return undefined; }
    })();
    fetch(statsEventsUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productId, event: 'view', visitorId }),
    }).catch(() => {});
  }, [productId]);

  // Stabilize selections: only change reference when actual key-value pairs differ
  const selectionsKey = configState
    ? JSON.stringify(configState.selections, Object.keys(configState.selections).sort())
    : '';
  const selectionsRef = useRef(configState?.selections);
  const stableSelections = useMemo(() => {
    if (!configState) return undefined;
    const prev = selectionsRef.current;
    // selectionsKey already captures value changes via sorted JSON
    selectionsRef.current = configState.selections;
    return configState.selections;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionsKey]);

  const warmSelectionsRef = useRef(stableSelections);
  warmSelectionsRef.current = stableSelections;

  const ruleActions: Array<{ type: string; targetId: string; value?: unknown }> = useMemo(() => {
    const actions = (configState?.derived?.ruleActions as any[]) || [];
    return actions;
  }, [configState?.derived?.ruleActions]);

  const modifierRuleEnableMap = useMemo(
    () => buildModifierRuleEnableMap(sceneData?.objects, ruleActions),
    [sceneData?.objects, ruleActions],
  );

  const modifierRuleParamMap = useMemo(
    () => buildModifierRuleParamMap(sceneData?.objects, ruleActions),
    [sceneData?.objects, ruleActions],
  );

  const materialOverrides = useMemo(() => {
    if (!stableSelections || !productModel) return {};
    return selectionsToMaterialOverrides(stableSelections, productModel.attributes);
  }, [stableSelections, productModel]);

  /** Rule object targets may be scene object id (preferred) or legacy display name — match either. */
  const { ruleHiddenObjectTargets, ruleHiddenTags } = useMemo(() => {
    const hidden = new Set<string>();
    const shown = new Set<string>();
    const hiddenTags = new Set<string>();
    const shownTags = new Set<string>();

    const parseVisibilityRefs = (raw: unknown): Array<{ kind: 'scene' | 'tag'; id: string }> => {
      if (!Array.isArray(raw)) return [];
      const out: Array<{ kind: 'scene' | 'tag'; id: string }> = [];
      for (const x of raw) {
        if (x == null || typeof x !== 'object') continue;
        const k = (x as { kind?: string }).kind;
        const id = (x as { id?: string }).id;
        if ((k === 'scene' || k === 'tag') && typeof id === 'string' && id.length > 0) {
          out.push({ kind: k, id });
        }
      }
      return out;
    };

    const applySetVisibility = (value: unknown) => {
      if (value == null || typeof value !== 'object') return;
      const v = value as { show?: unknown; hide?: unknown };
      for (const t of parseVisibilityRefs(v.hide)) {
        if (t.kind === 'tag') hiddenTags.add(t.id);
        else hidden.add(t.id);
      }
      for (const t of parseVisibilityRefs(v.show)) {
        if (t.kind === 'tag') shownTags.add(t.id);
        else shown.add(t.id);
      }
    };

    for (const action of ruleActions) {
      if (action.type === 'set-visibility') {
        applySetVisibility(action.value);
        continue;
      }
      if (action.type === 'hide-object' || action.type === 'hide-component') hidden.add(action.targetId);
      if (action.type === 'show-object' || action.type === 'show-component') shown.add(action.targetId);
      if (action.type === 'hide-tagged') hiddenTags.add(action.targetId);
      if (action.type === 'show-tagged') shownTags.add(action.targetId);
    }
    for (const id of shown) hidden.delete(id);
    for (const tag of shownTags) hiddenTags.delete(tag);
    return { ruleHiddenObjectTargets: hidden, ruleHiddenTags: hiddenTags };
  }, [ruleActions]);

  const materialGraphOverridesMap = useMemo(() => {
    if (!sceneData?.objects || !stableSelections) return {};
    // Defer graph evaluation until rule engine has resolved — prevents
    // eagerly loading textures for objects that will be hidden by rules.
    if (!configState?.derived) return {};
    const attrDefs = productModel?.attributes?.map((a: any) => ({
      key: a.key,
      type: a.type,
      defaultValue: a.defaultValue ?? null,
      options: a.options || a.values || [],
    })) ?? [];
    const result: Record<string, Record<string, Record<string, any>> | null> = {};

    // Editor outliner "hidden" is `object.visible` (+ parent chain) in scene JSON from the API.
    // Rule visibility is separate. Both must gate material graphs / PBR assignments — otherwise we
    // still call evaluateMaterialGraph (which hits TextureLoader) for objects that never mount.
    const objById = new Map(sceneData.objects.map((o) => [o.id, o as RuleVisibilitySceneObject]));
    const editorEffectiveVisible = buildEffectiveVisibleMap(sceneData.objects);
    /**
     * After first reveal (desktop) we keep rule-hidden GLBs mounted. They need graph materials
     * applied while hidden — otherwise first unhide evaluates + loads maps and the object
     * "builds" in. On mobile/iPad this OOMs Safari, so only warm currently rule-visible meshes.
     * Before reveal, still skip rule-hidden so unused variants don't compete with first paint.
     */
    const shouldApplyMaterialsToObjectId = (oid: string) => {
      if (editorEffectiveVisible.get(oid) === false) return false;
      const o = objById.get(oid);
      const ruleVis = o
        ? sceneObjectRuleVisible(o, ruleHiddenObjectTargets, ruleHiddenTags, objById)
        : true;
      // Exclusive finishes: skip bake while hidden until visited or background-warming.
      if (!ruleVis && exclusiveFinishMountIds?.has(oid)) {
        const root = exclusiveFinishRootById.get(oid);
        if (!(root && (visitedExclusiveRoots.has(root) || bgWarmExclusiveRoot === root))) {
          return false;
        }
      }
      if (firstRevealReady && !deviceInfo.isMobile) return true;
      // Mobile: also warm materials on the pinned Basic shell while it stays mounted-but-hidden
      // (e.g. during leather), so wood/carpaint maps are ready on the next swap.
      if (firstRevealReady && mobilePinnedMountIds?.has(oid)) return true;
      return ruleVis;
    };

    // 1) Library material assignments (from scene metadata)
    const assignments = (sceneData.metadata as any)?.materialAssignments as
      Array<{
        materialId: string;
        targets: Array<{ type: string; objectId?: string; meshName?: string; tagId?: string; groupObjectId?: string }>;
      }> | undefined;

    if (assignments && libraryMaterials.length > 0) {
      for (const assignment of assignments) {
        const mat = libraryMaterials.find((m: any) => m.id === assignment.materialId);
        if (!mat) continue;
        if (!assignment.targets?.length) continue;

        if (mat.type === 'pbr') {
          const overrides = { ...mat.data };
          stampSkipToneMapFromLibraryName(overrides, mat.name);
          if (Object.keys(overrides).length === 0) continue;
          for (const target of assignment.targets) {
            if (target.type === 'object' && target.objectId) {
              if (!shouldApplyMaterialsToObjectId(target.objectId)) continue;
              const prev = result[target.objectId] ? { ...result[target.objectId]! } : {};
              prev['*'] = { ...(prev['*'] ?? {}), ...overrides };
              result[target.objectId] = prev;
            } else if (target.type === 'mesh' && target.objectId && target.meshName) {
              if (!shouldApplyMaterialsToObjectId(target.objectId)) continue;
              const prev = result[target.objectId] ? { ...result[target.objectId]! } : {};
              prev[target.meshName] = { ...(prev[target.meshName] ?? {}), ...overrides };
              result[target.objectId] = prev;
            } else if (target.type === 'group' && target.groupObjectId) {
              for (const oid of collectGroupMaterialReceiverIds(sceneData.objects as any, target.groupObjectId)) {
                if (!shouldApplyMaterialsToObjectId(oid)) continue;
                const prev = result[oid] ? { ...result[oid]! } : {};
                prev['*'] = { ...(prev['*'] ?? {}), ...overrides };
                result[oid] = prev;
              }
            } else if (target.type === 'tag' && target.tagId) {
              for (const obj of sceneData.objects) {
                const objTags = (obj.metadata as any)?.tags as string[] | undefined;
                if (!objTags?.includes(target.tagId)) continue;
                if (!shouldApplyMaterialsToObjectId(obj.id)) continue;
                const prev = result[obj.id] ? { ...result[obj.id]! } : {};
                prev['*'] = { ...(prev['*'] ?? {}), ...overrides };
                result[obj.id] = prev;
              }
            }
          }
          continue;
        }

        if (mat.type !== 'graph') continue;
        const graph = mat.data;
        if (!graph?.nodes?.length) continue;

        let perMeshUv = false;
        try {
          perMeshUv = !deviceInfo.isMobile && materialGraphUsesPerMeshUvRandomize(graph);
        } catch {
          perMeshUv = false;
        }

        if (!perMeshUv) {
          // Collect target object IDs first; skip graph eval if none are visible
          const targetOids: string[] = [];
          for (const target of assignment.targets) {
            if ((target.type === 'object' || target.type === 'mesh') && target.objectId) {
              targetOids.push(target.objectId);
            } else if (target.type === 'group' && target.groupObjectId) {
              for (const oid of collectGroupMaterialReceiverIds(sceneData.objects as any, target.groupObjectId)) targetOids.push(oid);
            } else if (target.type === 'tag' && target.tagId) {
              for (const obj of sceneData.objects) {
                if ((obj.metadata as any)?.tags?.includes(target.tagId)) targetOids.push(obj.id);
              }
            }
          }
          if (targetOids.length > 0 && !targetOids.some(shouldApplyMaterialsToObjectId)) { continue; }

          let overrides: Record<string, any> = {};
          try {
            overrides = evaluateMaterialGraph(graph, stableSelections, attrDefs, { libraryName: mat.name }).overrides;
          } catch {
            continue;
          }
          if (Object.keys(overrides).length === 0) continue;
          overrides.__c3dShareKey = `lib:${mat.id}`;
          for (const target of assignment.targets) {
            if (target.type === 'object' && target.objectId) {
              if (!shouldApplyMaterialsToObjectId(target.objectId)) continue;
              const prev = result[target.objectId] ? { ...result[target.objectId]! } : {};
              prev['*'] = { ...(prev['*'] ?? {}), ...overrides };
              result[target.objectId] = prev;
            } else if (target.type === 'mesh' && target.objectId && target.meshName) {
              if (!shouldApplyMaterialsToObjectId(target.objectId)) continue;
              const prev = result[target.objectId] ? { ...result[target.objectId]! } : {};
              prev[target.meshName] = { ...(prev[target.meshName] ?? {}), ...overrides };
              result[target.objectId] = prev;
            } else if (target.type === 'group' && target.groupObjectId) {
              for (const oid of collectGroupMaterialReceiverIds(sceneData.objects as any, target.groupObjectId)) {
                if (!shouldApplyMaterialsToObjectId(oid)) continue;
                const prev = result[oid] ? { ...result[oid]! } : {};
                prev['*'] = { ...(prev['*'] ?? {}), ...overrides };
                result[oid] = prev;
              }
            } else if (target.type === 'tag' && target.tagId) {
              for (const obj of sceneData.objects) {
                const objTags = (obj.metadata as any)?.tags as string[] | undefined;
                if (!objTags?.includes(target.tagId)) continue;
                if (!shouldApplyMaterialsToObjectId(obj.id)) continue;
                const prev = result[obj.id] ? { ...result[obj.id]! } : {};
                prev['*'] = { ...(prev['*'] ?? {}), ...overrides };
                result[obj.id] = prev;
              }
            }
          }
          continue;
        }

        for (const target of assignment.targets) {
          if (target.type === 'tag' && target.tagId) {
            for (const obj of sceneData.objects) {
              const objTags = (obj.metadata as any)?.tags as string[] | undefined;
              if (!objTags?.includes(target.tagId)) continue;
              if (!shouldApplyMaterialsToObjectId(obj.id)) continue;
              let overrides: Record<string, any> = {};
              try {
                overrides = evaluateMaterialGraph(graph, stableSelections, attrDefs, {
                  meshName: `${obj.id}:tag:${target.tagId}`,
                  libraryName: mat.name,
                }).overrides;
              } catch {
                continue;
              }
              if (Object.keys(overrides).length === 0) continue;
              // Per-object UV random — do not share across Cap/Body.
              const prev = result[obj.id] ? { ...result[obj.id]! } : {};
              prev['*'] = { ...(prev['*'] ?? {}), ...overrides };
              result[obj.id] = prev;
            }
            continue;
          }

          if (target.type === 'group' && target.groupObjectId) {
            for (const oid of collectGroupMaterialReceiverIds(sceneData.objects as any, target.groupObjectId)) {
              if (!shouldApplyMaterialsToObjectId(oid)) continue;
              let go: Record<string, any> = {};
              try {
                go = evaluateMaterialGraph(graph, stableSelections, attrDefs, { meshName: '*', libraryName: mat.name }).overrides;
              } catch {
                continue;
              }
              if (Object.keys(go).length === 0) continue;
              const prev = result[oid] ? { ...result[oid]! } : {};
              prev['*'] = { ...(prev['*'] ?? {}), ...go };
              result[oid] = prev;
            }
            continue;
          }

          if ((target.type === 'object' || target.type === 'mesh') && target.objectId && !shouldApplyMaterialsToObjectId(target.objectId)) continue;

          const meshCtx =
            target.type === 'mesh' && target.meshName
              ? target.meshName
              : target.type === 'object'
                ? '*'
                : '';
          let overrides: Record<string, any> = {};
          try {
            overrides = evaluateMaterialGraph(graph, stableSelections, attrDefs, { meshName: meshCtx, libraryName: mat.name }).overrides;
          } catch {
            continue;
          }
          if (Object.keys(overrides).length === 0) continue;
          if (target.type === 'object' && target.objectId) {
            const prev = result[target.objectId] ? { ...result[target.objectId]! } : {};
            prev['*'] = { ...(prev['*'] ?? {}), ...overrides };
            result[target.objectId] = prev;
          } else if (target.type === 'mesh' && target.objectId && target.meshName) {
            const prev = result[target.objectId] ? { ...result[target.objectId]! } : {};
            prev[target.meshName] = { ...(prev[target.meshName] ?? {}), ...overrides };
            result[target.objectId] = prev;
          }
        }
      }
    }

    // 2) Legacy per-object materialGraph (fallback)
    for (const obj of sceneData.objects) {
      if (result[obj.id]) continue;
      if (!shouldApplyMaterialsToObjectId(obj.id)) continue;
      const graph = (obj.metadata as any)?.materialGraph;
      if (!graph || !graph.nodes?.length) continue;

      let perMeshUv = false;
      try {
        perMeshUv = !deviceInfo.isMobile && materialGraphUsesPerMeshUvRandomize(graph);
      } catch {
        perMeshUv = false;
      }

      try {
        if (!perMeshUv) {
          const { overrides } = evaluateMaterialGraph(graph, stableSelections, attrDefs);
          if (Object.keys(overrides).length > 0) {
            const byMesh: Record<string, Record<string, any>> = {};
            if (graph.meshTargets?.length) {
              for (const meshName of graph.meshTargets) {
                byMesh[meshName] = overrides;
              }
            } else {
              byMesh['*'] = overrides;
            }
            result[obj.id] = byMesh;

            const tagTargets: string[] = graph.tagTargets || [];
            if (tagTargets.length > 0) {
              for (const other of sceneData.objects) {
                if (other.id === obj.id) continue;
                const otherTags = (other.metadata as any)?.tags as string[] | undefined;
                if (!otherTags?.some((t: string) => tagTargets.includes(t))) continue;
                if (!shouldApplyMaterialsToObjectId(other.id)) continue;
                if (result[other.id]) continue;
                const prev: Record<string, Record<string, any>> = {};
                prev['*'] = overrides;
                result[other.id] = prev;
              }
            }
          }
        } else {
          const byMesh: Record<string, Record<string, any>> = {};
          if (graph.meshTargets?.length) {
            for (const meshName of graph.meshTargets) {
              const { overrides } = evaluateMaterialGraph(graph, stableSelections, attrDefs, { meshName });
              if (Object.keys(overrides).length > 0) byMesh[meshName] = overrides;
            }
          } else {
            const { overrides } = evaluateMaterialGraph(graph, stableSelections, attrDefs, { meshName: '' });
            byMesh['*'] = overrides;
          }
          if (Object.keys(byMesh).length > 0) {
            result[obj.id] = byMesh;

            const tagTargets: string[] = graph.tagTargets || [];
            if (tagTargets.length > 0) {
              for (const other of sceneData.objects) {
                if (other.id === obj.id) continue;
                const otherTags = (other.metadata as any)?.tags as string[] | undefined;
                if (!otherTags?.some((t: string) => tagTargets.includes(t))) continue;
                if (!shouldApplyMaterialsToObjectId(other.id)) continue;
                if (result[other.id]) continue;
                try {
                  const { overrides } = evaluateMaterialGraph(graph, stableSelections, attrDefs, {
                    meshName: `${other.id}:tag`,
                  });
                  if (Object.keys(overrides).length === 0) continue;
                  const prev: Record<string, Record<string, any>> = {};
                  prev['*'] = overrides;
                  result[other.id] = prev;
                } catch { /* ignore */ }
              }
            }
          }
        }
      } catch { /* ignore */ }
    }

    // 3) Rules: apply-material (runs after library + legacy; later actions override the same object)
    if (libraryMaterials.length > 0 && ruleActions.length > 0) {
      for (const action of ruleActions) {
        if (action.type !== 'apply-material' || !action.targetId || action.value == null) continue;
        const matKey = String(action.value);
        const mat = libraryMaterials.find((m: any) => m.id === matKey || m.name === matKey);
        if (!mat) continue;
        const receiverIds = new Set<string>();
        const tid = action.targetId as string;
        if (tid.startsWith('tag:')) {
          const tagId = tid.slice(4);
          for (const o of sceneData.objects) {
            const mTags = (o.metadata as any)?.tags as string[] | undefined;
            const rTags = (o as any).tags as string[] | undefined;
            const all = new Set([...(mTags || []), ...(rTags || [])]);
            if (!all.has(tagId)) continue;
            if (o.type === 'mesh' || o.type === 'primitive') receiverIds.add(o.id);
          }
        } else {
          const named = sceneData.objects.filter((o: any) => o.id === tid || o.name === tid);
          for (const t of named) {
            if (t.type === 'group') {
              for (const oid of collectGroupMaterialReceiverIds(sceneData.objects as any, t.id)) receiverIds.add(oid);
            } else if (t.type === 'mesh' || t.type === 'primitive') {
              receiverIds.add(t.id);
            }
          }
        }
        if (receiverIds.size === 0) continue;
        // Remove rule- or editor-hidden objects — no need to eval graphs or load textures for them
        for (const oid of [...receiverIds]) {
          if (!shouldApplyMaterialsToObjectId(oid)) receiverIds.delete(oid);
        }
        if (receiverIds.size === 0) continue;

        if (mat.type === 'pbr') {
          const overrides = { ...mat.data };
          stampSkipToneMapFromLibraryName(overrides, mat.name);
          if (Object.keys(overrides).length === 0) continue;
          for (const oid of receiverIds) {
            const prev = result[oid] ? { ...result[oid]! } : {};
            // Replace wildcard material — do not merge with prior library graph (e.g. Carpaint + Color attr leaking into Wood).
            prev['*'] = overrides;
            result[oid] = prev;
          }
          continue;
        }

        if (mat.type !== 'graph') continue;
        const graph = mat.data;
        if (!graph?.nodes?.length) continue;

        let rulePerMeshUv = false;
        try {
          // iPad: per-mesh wood eval doubles CPU bake (Body+Cap) and crashes Safari.
          rulePerMeshUv =
            !deviceInfo.isMobile && materialGraphUsesPerMeshUvRandomize(graph);
        } catch {
          rulePerMeshUv = false;
        }

        if (!rulePerMeshUv) {
          let overrides: Record<string, any> = {};
          try {
            overrides = evaluateMaterialGraph(graph, stableSelections, attrDefs, { libraryName: mat.name }).overrides;
          } catch {
            continue;
          }
          if (Object.keys(overrides).length === 0) continue;
          // Cap + Body share one THREE material so tint swipes stay locked (carpaint).
          overrides.__c3dShareKey = `rule:${mat.id}`;
          for (const oid of receiverIds) {
            const prev = result[oid] ? { ...result[oid]! } : {};
            prev['*'] = overrides;
            result[oid] = prev;
          }
          continue;
        }

        for (const oid of receiverIds) {
          const byMesh: Record<string, Record<string, any>> = {};
          if (graph.meshTargets?.length) {
            for (const meshName of graph.meshTargets) {
              let ov: Record<string, any> = {};
              try {
                ov = evaluateMaterialGraph(graph, stableSelections, attrDefs, { meshName, libraryName: mat.name }).overrides;
              } catch {
                continue;
              }
              if (Object.keys(ov).length > 0) byMesh[meshName] = ov;
            }
          } else {
            let ov: Record<string, any> = {};
            try {
              ov = evaluateMaterialGraph(graph, stableSelections, attrDefs, { meshName: `${oid}:rule`, libraryName: mat.name }).overrides;
            } catch {
              continue;
            }
            if (Object.keys(ov).length > 0) byMesh['*'] = ov;
          }
          if (Object.keys(byMesh).length === 0) continue;
          result[oid] = byMesh;
        }
      }
    }

    const graphFreezeKey = `${selectionsKey}|${libraryMaterials.map((m: { id: string }) => m.id).join(',')}`;
    if (graphFreezeKey !== frozenVisibleGraphSelRef.current) {
      frozenVisibleGraphSelRef.current = graphFreezeKey;
      frozenVisibleGraphRef.current = null;
    }
    if (!firstRevealReady) {
      frozenVisibleGraphRef.current = result;
      return result;
    }
    const frozen = frozenVisibleGraphRef.current;
    if (frozen) {
      const merged: typeof result = { ...result };
      for (const oid of Object.keys(frozen)) {
        merged[oid] = frozen[oid];
      }
      frozenVisibleGraphRef.current = merged;
      return merged;
    }
    frozenVisibleGraphRef.current = result;
    return result;
  }, [sceneData?.objects, sceneData?.metadata, stableSelections, productModel, libraryMaterials, ruleActions, ruleHiddenObjectTargets, ruleHiddenTags, configState?.derived, firstRevealReady, selectionsKey, deviceInfo.isMobile, mobilePinnedMountIds, exclusiveFinishMountIds, exclusiveFinishRootById, visitedExclusiveRoots, bgWarmExclusiveRoot]);

  const visibleModels = useMemo(() => {
    if (!stableSelections) return assets.models;

    const variantAssetIds = new Set<string>();
    const selectedAssetUrls = new Set<string>();
    let hasAnyVariantAssets = false;

    for (const [compId, variantMap] of Object.entries(componentVariants)) {
      for (const [, assetInfo] of Object.entries(variantMap)) {
        if (assetInfo) {
          hasAnyVariantAssets = true;
          variantAssetIds.add(assetInfo.assetId);
        }
      }
      const selectedVariant = stableSelections[`component:${compId}`] as string | undefined;
      if (selectedVariant && variantMap[selectedVariant]) {
        selectedAssetUrls.add(variantMap[selectedVariant]!.url);
      }
    }

    if (!hasAnyVariantAssets) return assets.models;

    return assets.models.filter((m) => {
      if (variantAssetIds.has(m.id)) {
        return selectedAssetUrls.has(m.url);
      }
      return true;
    });
  }, [assets.models, stableSelections, componentVariants]);

  const catalogAssetLineupKey = useMemo(
    () => visibleModels.map((m) => `${m.id}\x1f${m.url}`).sort().join('\x1e'),
    [visibleModels],
  );

  const totalPrice = useMemo(() => {
    if (!productModel || !configState) return null;
    const meta = productModel.metadata as any;
    const basePrice = typeof meta?.basePrice === 'number' ? meta.basePrice : null;
    if (basePrice === null) return null;

    let price = basePrice;
    const comps = productModel.components ?? [];
    for (const comp of comps) {
      const selectedVariantId = configState.selections[`component:${comp.id}`] as string | undefined;
      if (!selectedVariantId || !componentVariants[comp.id]) continue;
      const variantAsset = componentVariants[comp.id][selectedVariantId];
      if (variantAsset && typeof (variantAsset as any).priceDelta === 'number') {
        price += (variantAsset as any).priceDelta;
      }
    }

    const currency = (meta?.currency as string) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(price);
    } catch {
      return `${currency} ${price.toFixed(2)}`;
    }
  }, [productModel, configState, componentVariants]);

  // --- All hooks must be called before any early returns (React Rules of Hooks) ---
  const [shareStatus, setShareStatus] = useState<'idle' | 'saving' | 'copied'>('idle');
  const [savedConfigId, setSavedConfigId] = useState<string | null>(null);
  const [annotations, setAnnotations] = useState<Array<{ id: string; text: string; author: string | null; createdAt: string }>>([]);
  const [newAnnotation, setNewAnnotation] = useState('');
  const [configNotes, setConfigNotes] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [animClips, setAnimClips] = useState<string[]>([]);
  const [animState, setAnimState] = useState<AnimationControls>({ playing: true, speed: 1 });
  const [fadedOutIds, setFadedOutIds] = useState<Set<string>>(new Set());
  const [loadStartTime] = useState(() => Date.now());
  const [activePreset, setActivePreset] = useState('perspective');
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [activeChip, setActiveChip] = useState<string | null>(null);
  const [isNarrowViewport, setIsNarrowViewport] = useState(false);
  const [mobileCfgCollapsed, setMobileCfgCollapsed] = useState(false);
  const [desktopCfgCollapsed, setDesktopCfgCollapsed] = useState(false);
  const toggleDesktopCfgCollapsed = useCallback(() => setDesktopCfgCollapsed((p) => !p), []);
  const [showSummary, setShowSummary] = useState(false);
  const [envPreset, setEnvPreset] = useState(sceneData?.environment?.preset || 'studio');
  const [autoRotateMode, setAutoRotateMode] = useState<AutoRotateMode>('off');
  const introEffect = useMemo(() => {
    const raw = (sceneData as any)?.metadata?.introEffect;
    if (raw === 'off' || raw === 'orbit') return raw as 'off' | 'orbit';
    return 'orbit';
  }, [(sceneData as any)?.metadata?.introEffect]);
  const [sceneStats, setSceneStats] = useState<SceneStats | null>(null);

  useEffect(() => {
    if (sceneData?.environment?.preset) {
      setEnvPreset(sceneData.environment.preset);
    }
  }, [sceneData]);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showComparison, setShowComparison] = useState(false);

  const isRuleVisible = useMemo(() => {
    const objects = sceneData?.objects;
    const byId = objects?.length ? new Map(objects.map((o) => [o.id, o as RuleVisibilitySceneObject])) : null;
    return (obj: SceneObjectData) =>
      sceneObjectRuleVisible(obj as RuleVisibilitySceneObject, ruleHiddenObjectTargets, ruleHiddenTags, byId);
  }, [sceneData?.objects, ruleHiddenObjectTargets, ruleHiddenTags]);

  /** Content-based signature — ruleHidden* are new Set refs every render; without this, fadedOutIds clears on every selection and causes a one-frame flash during opacity fades. */
  const ruleVisibilitySignature = useMemo(
    () =>
      `${[...ruleHiddenObjectTargets].sort().join('\x1e')}\x1f${[...ruleHiddenTags].sort().join('\x1e')}`,
    [ruleHiddenObjectTargets, ruleHiddenTags],
  );

  /**
   * Object show/hide snaps. Opacity crossfades (simultaneous or out→in) still leave
   * Cap/cage/liquid frankenstein composites on this product — keep material swaps crisp.
   */
  const visibilityTransitionDuration = 0;

  /** Mild material settle — rule `set-transition` can still author a custom length. */
  const materialTransitionDuration = useMemo(() => {
    // iPad/phone: crossfades + deferred map holds make finishes feel sticky (wood "never arrives").
    if (deviceInfo.isMobile) return 0;
    for (const action of ruleActions) {
      if (action.type === 'set-transition' && action.value != null) {
        const v = typeof action.value === 'object' ? (action.value as any).duration : Number(action.value);
        if (typeof v === 'number' && Number.isFinite(v) && v >= 0) {
          if (v === 0) return 0;
          return Math.min(v, 0.5);
        }
      }
    }
    return 0.4;
  }, [ruleActions, deviceInfo.isMobile]);

  /** Sync faded-out ids to the current rule-hidden set — visibility snaps, no fade wait. */
  useEffect(() => {
    setFadedOutIds(() => {
      const objects = sceneData?.objects;
      if (!objects?.length) return new Set();
      const byId = new Map(objects.map((o) => [o.id, o as RuleVisibilitySceneObject]));
      const next = new Set<string>();
      for (const obj of objects) {
        if (
          !sceneObjectRuleVisible(
            obj as RuleVisibilitySceneObject,
            ruleHiddenObjectTargets,
            ruleHiddenTags,
            byId,
          )
        ) {
          next.add(obj.id);
        }
      }
      return next;
    });
    // Intentionally only signature: it encodes hidden-set content.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid re-running when sceneData.objects identity churns
  }, [ruleVisibilitySignature]);

  const disabledOptionsByAttr = useMemo(() => {
    const result: Record<string, Set<string>> = {};
    for (const action of ruleActions) {
      if (action.type === 'disable-option' && action.targetId && action.value) {
        if (!result[action.targetId]) result[action.targetId] = new Set();
        result[action.targetId].add(String(action.value));
      }
    }
    return result;
  }, [ruleActions]);
  const ruleActionsKey = useMemo(() => JSON.stringify(ruleActions), [ruleActions]);
  const transitionSyncKey = `${selectionsKey}\x1f${ruleActionsKey}`;
  // Arm during render (freeze clock); commit after Cap/Body layouts so they start together.
  armMaterialTransitionClock(transitionSyncKey, materialTransitionDuration);
  useLayoutEffect(() => {
    commitLayoutMaterialTransitionClock();
  }, [transitionSyncKey, materialTransitionDuration]);
  const sharedTransitionStartedAt = getMaterialTransitionStartedAt();

  /**
   * Objects rule-hidden on first derived state must not mount GlbModel even for one frame:
   * fadedOutIds is filled in useEffect (after paint), so previously we mounted every object with
   * opacity 0 — useGLTF still downloaded full GLBs + embedded textures (e.g. shadow planes).
   * Freeze initial rule-hidden ids for the product session; still mount when user later hides
   * something (fade-out) because those ids are not in the frozen set.
   */
  const frozenInitiallyRuleHiddenIdsRef = React.useRef<Set<string> | null>(null);
  const frozenInitProductIdRef = React.useRef(productId);
  if (frozenInitProductIdRef.current !== productId) {
    frozenInitProductIdRef.current = productId;
    frozenInitiallyRuleHiddenIdsRef.current = null;
  }
  if (
    sceneData?.objects?.length &&
    configState?.derived &&
    frozenInitiallyRuleHiddenIdsRef.current === null
  ) {
    const byId = new Map(sceneData.objects.map((o) => [o.id, o as RuleVisibilitySceneObject]));
    const s = new Set<string>();
    for (const o of sceneData.objects) {
      if (!sceneObjectRuleVisible(o, ruleHiddenObjectTargets, ruleHiddenTags, byId)) {
        s.add(o.id);
      }
    }
    frozenInitiallyRuleHiddenIdsRef.current = s;
  }

  /**
   * Shared cage shell + engraving — needed by wood/carpaint/straw/metal-mesh.
   * Leather/rubber unmount until first visit, then stay mounted-but-hidden so
   * return trips are instant (avoids slow remount + false carpaint bridge).
   */
  const [keepAllModelsMounted, setKeepAllModelsMounted] = useState(false);
  useEffect(() => {
    if (!firstRevealReady || deviceInfo.isMobile) {
      setKeepAllModelsMounted(false);
      return;
    }
    const id = window.setTimeout(() => setKeepAllModelsMounted(true), 50);
    return () => window.clearTimeout(id);
  }, [firstRevealReady, deviceInfo.isMobile]);

  const sceneRuleDrawableShouldMount = useCallback(
    (obj: SceneObjectData) => {
      if (isRuleVisible(obj)) return true;
      if (exclusiveFinishMountIds?.has(obj.id)) {
        const root = exclusiveFinishRootById.get(obj.id);
        if (root && (visitedExclusiveRoots.has(root) || bgWarmExclusiveRoot === root)) return true;
        return false;
      }
      if (keepAllModelsMounted) return true;
      if (mobilePinnedMountIds?.has(obj.id)) return true;
      return false;
    },
    [
      isRuleVisible,
      keepAllModelsMounted,
      mobilePinnedMountIds,
      exclusiveFinishMountIds,
      exclusiveFinishRootById,
      visitedExclusiveRoots,
      bgWarmExclusiveRoot,
    ],
  );

  const ruleCameraTarget = useMemo(() => {
    for (const action of ruleActions) {
      if (action.type === 'camera-move' && action.targetId) {
        const raw = action.value;
        const dur = typeof raw === 'object' && raw ? (raw as any).duration : Number(raw);
        return { cameraId: action.targetId, duration: Number.isFinite(dur) && dur > 0 ? dur : 0.5 };
      }
    }
    return null;
  }, [ruleActions]);

  const lastRuleCamRef = useRef<string | null>(null);
  useEffect(() => {
    if (!ruleCameraTarget) {
      lastRuleCamRef.current = null;
      return;
    }
    if (lastRuleCamRef.current === ruleCameraTarget.cameraId) return;
    lastRuleCamRef.current = ruleCameraTarget.cameraId;

    const cameras = (sceneData as any)?.metadata?.cameras as any[] | undefined;
    const cam = cameras?.find((c: any) => c.id === ruleCameraTarget.cameraId || c.name === ruleCameraTarget.cameraId);
    if (!cam?.position) return;

    const canvas = document.querySelector('[data-viewer] canvas');
    if (!canvas) return;
    canvas.dispatchEvent(new CustomEvent('camera-transition', {
      detail: {
        position: [cam.position.x, cam.position.y, cam.position.z],
        target: cam.target ? [cam.target.x, cam.target.y, cam.target.z] : undefined,
        duration: ruleCameraTarget.duration,
      },
    }));
  }, [ruleCameraTarget, sceneData]);

  const sceneObjectEffectiveVisible = useMemo(() => {
    if (!sceneData?.objects?.length) return new Map<string, boolean>();
    return buildEffectiveVisibleMap(sceneData.objects);
  }, [sceneData?.objects]);

  const sceneModelObjects = useMemo(() => {
    if (!sceneData) return [];
    return sceneData.objects.filter(
      (o) =>
        o.asset?.url &&
        o.type !== 'gsplat' &&
        !isSplatAssetFormat((o.asset as any)?.format) &&
        !isSplatAssetUrl(o.asset.url) &&
        sceneObjectEffectiveVisible.get(o.id) !== false,
    );
  }, [sceneData, sceneObjectEffectiveVisible]);

  const sceneGsplatObjects = useMemo(() => {
    if (!sceneData) return [];
    return sceneData.objects.filter(
      (o) =>
        o.asset?.url &&
        (o.type === 'gsplat' || isSplatAssetFormat((o.asset as any)?.format) || isSplatAssetUrl(o.asset.url)) &&
        sceneObjectEffectiveVisible.get(o.id) !== false,
    );
  }, [sceneData, sceneObjectEffectiveVisible]);

  const scenePrimitives = useMemo(() => {
    if (!sceneData) return [];
    return sceneData.objects.filter(
      (o) =>
        o.type === 'primitive' &&
        (o.metadata as any)?.primitiveShape &&
        sceneObjectEffectiveVisible.get(o.id) !== false,
    );
  }, [sceneData, sceneObjectEffectiveVisible]);

  const sceneLights = useMemo(() => {
    if (!sceneData) return [];
    return sceneData.objects.filter(
      (o) =>
        o.type === 'light' &&
        (o.metadata as any)?.lightConfig &&
        isRuleVisible(o) &&
        sceneObjectEffectiveVisible.get(o.id) !== false,
    );
  }, [sceneData, sceneObjectEffectiveVisible, isRuleVisible]);
  const envRotationTagId = (sceneData as any)?.metadata?.envRotationTagId as string | undefined;
  const envLinkToCamera = sceneData?.environment?.linkToCamera === true;
  const isObjTagged = (obj: any) => envRotationTagId && (obj.metadata?.tags as string[] | undefined)?.includes(envRotationTagId);
  const sceneMountTargets = useMemo(
    () => [...sceneModelObjects, ...sceneGsplatObjects].filter((o) => sceneRuleDrawableShouldMount(o)),
    [sceneModelObjects, sceneGsplatObjects, sceneRuleDrawableShouldMount],
  );
  const sceneGlbLoadTargetCount = sceneMountTargets.length;
  /** When object ids/urls change (new publish), not when rule visibility changes target *count*. */
  const sceneGlbLineupResetKey = useMemo(() => {
    if (!sceneData?.objects?.length) return '';
    return sceneData.objects
      .filter((o) => o.asset?.url)
      .map((o) => `${o.id}\x1f${o.asset!.url}`)
      .sort()
      .join('\x1e');
  }, [sceneData?.objects]);
  const sceneMountTargetIdsKey = useMemo(
    () => sceneMountTargets.map((o) => o.id).sort().join('\x1e'),
    [sceneMountTargets],
  );
  const [sceneGlbLoadedById, setSceneGlbLoadedById] = useState<Record<string, boolean>>({});
  const [catalogModelsLoaded, setCatalogModelsLoaded] = useState(0);

  useEffect(() => {
    setSceneGlbLoadedById({});
  }, [productId, sceneGlbLineupResetKey]);

  useEffect(() => {
    const allow = new Set(sceneMountTargets.map((o) => o.id));
    setSceneGlbLoadedById((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const id of Object.keys(next)) {
        if (!allow.has(id)) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [sceneMountTargetIdsKey]);

  const sceneModelsLoaded = useMemo(
    () => sceneMountTargets.filter((o) => sceneGlbLoadedById[o.id]).length,
    [sceneMountTargets, sceneGlbLoadedById],
  );

  const hasSceneModels = sceneModelObjects.length > 0 || sceneGsplatObjects.length > 0 || scenePrimitives.length > 0;
  const totalModels = hasSceneModels ? sceneGlbLoadTargetCount : visibleModels.length;
  const modelsLoaded = hasSceneModels ? sceneModelsLoaded : catalogModelsLoaded;
  const allLoaded = totalModels === 0 || modelsLoaded >= totalModels;
  const loadProgress = totalModels > 0 ? Math.round((modelsLoaded / totalModels) * 100) : 0;

  /** Full-screen / blocking viewer chrome only until the first "current targets" wave completes; not on every rule swap. */
  const blockingLoadResetKey = useMemo(
    () => `${productId}\x1e${hasSceneModels ? sceneGlbLineupResetKey : catalogAssetLineupKey}`,
    [productId, hasSceneModels, sceneGlbLineupResetKey, catalogAssetLineupKey],
  );
  const [blockingLoadUiDismissed, setBlockingLoadUiDismissed] = useState(false);
  const [viewerFadeIn, setViewerFadeIn] = useState(false);
  const [contentReadyById, setContentReadyById] = useState<Record<string, boolean>>({});
  const graphOverridesArrivedRef = useRef(false);

  useEffect(() => {
    setBlockingLoadUiDismissed(false);
    setFirstRevealReady(false);
    setViewerFadeIn(false);
    setContentReadyById({});
    graphOverridesArrivedRef.current = false;
  }, [blockingLoadResetKey]);

  // Graph overrides often arrive after the first GLB parse. Reset once on that empty→ready
  // transition, not on every override-map identity change. Texture bakes and rule settling
  // rebuild this object repeatedly; clearing readiness each time caused 12–45 s loader stalls.
  useEffect(() => {
    if (firstRevealReady) return;
    const hasOverrides = Object.values(materialGraphOverridesMap).some(
      (entry) => entry != null && Object.keys(entry).length > 0,
    );
    if (!hasOverrides || graphOverridesArrivedRef.current) return;
    graphOverridesArrivedRef.current = true;
    setContentReadyById({});
  }, [materialGraphOverridesMap, firstRevealReady]);

  const modelsWaveDone = totalModels === 0 || allLoaded;
  const rulesReady = !!configState?.derived;
  const hasGraphMaterials = libraryMaterials.some(
    (m: any) => m.type === 'graph' && Array.isArray(m.data?.nodes) && m.data.nodes.length > 0,
  );

  /**
   * When leather/rubber remount on first select, hold the whole tree until ready.
   * Do NOT bridge with Basic — that left carpaint on screen and felt like the
   * wrong material “won”. After first visit, finishes stay mounted so swaps snap.
   */
  const [finishRevealHoldRoot, setFinishRevealHoldRoot] = useState<string | null>(null);
  const finishRevealHoldRootRef = useRef<string | null>(null);
  finishRevealHoldRootRef.current = finishRevealHoldRoot;
  const finishRevealPendingRef = useRef<Set<string>>(new Set());
  const finishRevealEpochRef = useRef(0);
  const prevVisibleExclusiveRootsRef = useRef<Set<string>>(new Set());

  const selectedMaterialKey = String(stableSelections?.material ?? '');

  const visibleExclusiveRoots = useMemo(() => {
    const roots = new Set<string>();
    if (!sceneData?.objects?.length || exclusiveFinishRootById.size === 0) return roots;
    for (const o of sceneData.objects) {
      if (!o.asset?.url) continue;
      const root = exclusiveFinishRootById.get(o.id);
      if (!root) continue;
      if (isRuleVisible(o as SceneObjectData)) roots.add(root);
    }
    return roots;
  }, [sceneData?.objects, exclusiveFinishRootById, isRuleVisible]);

  const clearFinishRevealHold = useCallback(() => {
    finishRevealPendingRef.current = new Set();
    setFinishRevealHoldRoot(null);
  }, []);

  useLayoutEffect(() => {
    const prev = prevVisibleExclusiveRootsRef.current;
    const newlyShown: string[] = [];
    for (const root of visibleExclusiveRoots) {
      if (!prev.has(root)) newlyShown.push(root);
    }
    prevVisibleExclusiveRootsRef.current = new Set(visibleExclusiveRoots);

    if (finishRevealHoldRoot && !visibleExclusiveRoots.has(finishRevealHoldRoot)) {
      clearFinishRevealHold();
    }

    // Selection moved away from the held finish (stale hold) — abort immediately.
    if (finishRevealHoldRoot) {
      const expected = exclusiveFinishMaterialByRoot.get(finishRevealHoldRoot);
      if (expected && selectedMaterialKey && selectedMaterialKey !== expected) {
        clearFinishRevealHold();
      }
    }

    if (newlyShown.length === 0 || !sceneData?.objects?.length) return;

    const root = newlyShown[0];
    const expectedMat = exclusiveFinishMaterialByRoot.get(root);
    if (expectedMat && selectedMaterialKey && selectedMaterialKey !== expectedMat) {
      return;
    }

    // Already warmed from a prior visit or background warm — no hold, just flip visible.
    if (visitedExclusiveRoots.has(root)) return;
    if (bgWarmExclusiveRoot === root) return;

    const pending = new Set<string>();
    for (const o of sceneData.objects) {
      if (!o.asset?.url) continue;
      if (exclusiveFinishRootById.get(o.id) !== root) continue;
      if (!isRuleVisible(o as SceneObjectData)) continue;
      if (sceneObjectEffectiveVisible.get(o.id) === false) continue;
      pending.add(o.id);
    }
    if (pending.size === 0) return;
    finishRevealEpochRef.current += 1;
    finishRevealPendingRef.current = pending;
    setFinishRevealHoldRoot(root);
    setContentReadyById((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const id of pending) {
        if (next[id]) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [
    visibleExclusiveRoots,
    sceneData?.objects,
    exclusiveFinishRootById,
    exclusiveFinishMaterialByRoot,
    isRuleVisible,
    sceneObjectEffectiveVisible,
    finishRevealHoldRoot,
    selectedMaterialKey,
    visitedExclusiveRoots,
    bgWarmExclusiveRoot,
    clearFinishRevealHold,
  ]);

  useEffect(() => {
    if (!finishRevealHoldRoot) return;
    const epoch = finishRevealEpochRef.current;
    const id = window.setTimeout(() => {
      if (finishRevealEpochRef.current !== epoch) return;
      const root = finishRevealHoldRoot;
      clearFinishRevealHold();
      if (root) {
        setVisitedExclusiveRoots((prev) => {
          if (prev.has(root)) return prev;
          const next = new Set(prev);
          next.add(root);
          return next;
        });
      }
    }, 4500);
    return () => window.clearTimeout(id);
  }, [finishRevealHoldRoot, clearFinishRevealHold]);

  const isExclusiveFinishRevealHeld = useCallback(
    (objId: string) => {
      if (!finishRevealHoldRoot) return false;
      return exclusiveFinishRootById.get(objId) === finishRevealHoldRoot;
    },
    [finishRevealHoldRoot, exclusiveFinishRootById],
  );

  const handleModelContentReady = useCallback((objectId: string) => {
    setContentReadyById((prev) => (prev[objectId] ? prev : { ...prev, [objectId]: true }));
    if (!finishRevealPendingRef.current.has(objectId)) return;
    finishRevealPendingRef.current.delete(objectId);
    if (finishRevealPendingRef.current.size > 0) return;
    const root = finishRevealHoldRootRef.current;
    clearFinishRevealHold();
    if (root) {
      setVisitedExclusiveRoots((prev) => {
        if (prev.has(root)) return prev;
        const next = new Set(prev);
        next.add(root);
        return next;
      });
    }
  }, [clearFinishRevealHold]);

  /** Background warm mount: mark root visited once every hidden mesh reports ready. */
  useEffect(() => {
    if (!bgWarmExclusiveRoot || !sceneData?.objects?.length) return;
    const root = bgWarmExclusiveRoot;
    if (visitedExclusiveRoots.has(root)) return;
    const needed: string[] = [];
    for (const o of sceneData.objects) {
      if (!o.asset?.url) continue;
      if (exclusiveFinishRootById.get(o.id) !== root) continue;
      if (sceneObjectEffectiveVisible.get(o.id) === false) continue;
      needed.push(o.id);
    }
    if (needed.length === 0) return;
    if (!needed.every((id) => contentReadyById[id])) return;
    setVisitedExclusiveRoots((prev) => {
      if (prev.has(root)) return prev;
      const next = new Set(prev);
      next.add(root);
      return next;
    });
  }, [
    bgWarmExclusiveRoot,
    contentReadyById,
    sceneData?.objects,
    exclusiveFinishRootById,
    sceneObjectEffectiveVisible,
    visitedExclusiveRoots,
  ]);

  const mountTargetsContentReady =
    sceneMountTargets.length === 0 ||
    sceneMountTargets.every((o) => contentReadyById[o.id]);
  const catalogContentReady =
    hasSceneModels ||
    visibleModels.length === 0 ||
    visibleModels.every((m) => contentReadyById[m.id]);

  // Opaque loader until models, rules, and per-model materials are ready.
  useEffect(() => {
    if (firstRevealReady) return;
    if (!modelsWaveDone || !rulesReady) return;
    if (hasSceneModels && sceneMountTargets.length > 0 && !mountTargetsContentReady) return;
    if (!hasSceneModels && !catalogContentReady) return;

    let cancelled = false;
    let raf1 = 0;
    let raf2 = 0;

    const reveal = () => {
      if (cancelled) return;
      setFirstRevealReady(true);
    };

    // Per-model holdUntilReady + materialsGateReady already waited for applied maps.
    // Extra global image/CPU waits added 2–3s without improving first paint.
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(reveal);
    });
    const maxWait = window.setTimeout(reveal, 400);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(maxWait);
    };
  }, [
    firstRevealReady,
    modelsWaveDone,
    rulesReady,
    hasSceneModels,
    mountTargetsContentReady,
    catalogContentReady,
    sceneMountTargets.length,
    blockingLoadResetKey,
  ]);

  // Never wedge on "Preparing your experience" if a single mesh never calls onContentReady
  // (404 texture, graph apply stall, etc.). GlbModel caps each model at ~4s; allow slack for N meshes.
  useEffect(() => {
    if (firstRevealReady) return;
    if (!modelsWaveDone || !rulesReady) return;
    const safetyMs = Math.min(20000, 5000 + sceneMountTargets.length * 250);
    const t = window.setTimeout(() => setFirstRevealReady(true), safetyMs);
    return () => clearTimeout(t);
  }, [firstRevealReady, modelsWaveDone, rulesReady, sceneMountTargets.length, blockingLoadResetKey]);

  useEffect(() => {
    if (!firstRevealReady || blockingLoadUiDismissed) return;
    setBlockingLoadUiDismissed(true);
  }, [firstRevealReady, blockingLoadUiDismissed]);

  // Fade the 3D view in one frame after the loader starts dismissing — avoids a hard cut.
  useEffect(() => {
    if (!firstRevealReady) return;
    const id = requestAnimationFrame(() => setViewerFadeIn(true));
    return () => cancelAnimationFrame(id);
  }, [firstRevealReady]);

  /**
   * After the page has revealed: download + silently mount exclusive finishes (leather/rubber)
   * one at a time while hidden, so the first user selection is not an empty wait.
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!firstRevealReady || !viewerFadeIn) return;
    if (!sceneData?.objects?.length || exclusiveFinishRoots.length === 0) return;

    let cancelled = false;
    const sleep = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));
    const waitVisited = async (root: string, timeoutMs: number) => {
      const start = performance.now();
      while (!cancelled && performance.now() - start < timeoutMs) {
        if (visitedExclusiveRootsRef.current.has(root)) return true;
        await sleep(120);
      }
      return visitedExclusiveRootsRef.current.has(root);
    };

    const run = async () => {
      // Let first paint / orbit settle before competing for network + GPU.
      await sleep(deviceInfo.isMobile ? 800 : 250);
      if (cancelled) return;

      const urls: string[] = [];
      for (const o of sceneData.objects) {
        if (!o.asset?.url) continue;
        if (exclusiveFinishMountIds?.has(o.id)) urls.push(o.asset.url);
      }
      if (urls.length) prefetchSceneModelAssetUrls(urls);

      for (const m of libraryMaterials) {
        if (m?.type !== 'graph' || !Array.isArray(m.data?.nodes)) continue;
        if (!/^(Leather|Rubber)/i.test(String(m?.name || ''))) continue;
        try {
          warmMaterialGraphTextureCache(collectMaterialGraphImageUrls(m.data));
        } catch {
          /* ignore */
        }
      }

      await sleep(deviceInfo.isMobile ? 500 : 200);
      if (cancelled) return;

      for (const root of exclusiveFinishRoots) {
        if (cancelled) return;
        if (visitedExclusiveRootsRef.current.has(root)) continue;
        // Skip if user already selected this finish (rules visible) — normal path owns it.
        const matKey = exclusiveFinishMaterialByRoot.get(root);
        if (matKey && String(stableSelections?.material ?? '') === matKey) {
          continue;
        }
        setBgWarmExclusiveRoot(root);
        const ok = await waitVisited(root, deviceInfo.isMobile ? 10000 : 8000);
        if (!ok && !cancelled) {
          // Timed out still mark visited so we keep the (partially) decoded mount.
          setVisitedExclusiveRoots((prev) => {
            if (prev.has(root)) return prev;
            const next = new Set(prev);
            next.add(root);
            return next;
          });
        }
        if (cancelled) return;
        setBgWarmExclusiveRoot(null);
        await sleep(350);
      }
    };

    void run();
    return () => {
      cancelled = true;
      setBgWarmExclusiveRoot(null);
    };
    // Intentionally omit visited/selections identity churn — loop reads refs + live material key.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- start once per reveal
  }, [
    firstRevealReady,
    viewerFadeIn,
    sceneData?.objects,
    exclusiveFinishRoots,
    exclusiveFinishMountIds,
    exclusiveFinishMaterialByRoot,
    libraryMaterials,
    deviceInfo.isMobile,
  ]);

  /**
   * After first reveal: warm remaining option GLBs/maps in the background so the
   * next finish swap is instant. Timing is scene metadata `playerWarmup`.
   * Mobile: only warm materials that paint the pinned Basic shell (wood/carpaint/…)
   * — not leather/rubber GLBs — so leather→wood does not wait on first decode.
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!firstRevealReady) return;
    if (!configState?.derived) return;

    const warmupMode = deviceInfo.isMobile
      ? 'late'
      : parsePlayerWarmupMode((sceneData as any)?.metadata?.playerWarmup);
    if (warmupMode === 'off') return;
    const delays = PLAYER_WARMUP_DELAYS[warmupMode];
    const mobileShellOnly = deviceInfo.isMobile;

    const glbId = window.setTimeout(() => {
      if (mobileShellOnly) {
        // Exclusive finishes are also warmed by the post-reveal bg-warm effect;
        // still prefetch here so network starts even if that effect is delayed.
        if (sceneData?.objects?.length && exclusiveFinishMountIds) {
          const urls: string[] = [];
          for (const o of sceneData.objects) {
            if (!o.asset?.url) continue;
            if (exclusiveFinishMountIds.has(o.id)) urls.push(o.asset.url);
          }
          if (urls.length) prefetchSceneModelAssetUrls(urls);
        }
        return;
      }
      const ruleGraphs = getRuleGraphs?.() ?? [];
      const plan = buildRuleCompPrefetchPlan(
        ruleGraphs,
        productModel,
        (stableSelections ?? configState.selections) as Record<string, unknown>,
      );
      if (sceneData?.objects?.length) {
        const urls: string[] = [];
        for (const o of sceneData.objects) {
          if (!o.asset?.url) continue;
          if (plan.sceneTargets.has(o.id) || plan.sceneTargets.has(o.name)) {
            urls.push(o.asset.url);
            continue;
          }
          const tags = (o.metadata as any)?.tags as string[] | undefined;
          if (tags?.some((t) => plan.tagTargets.has(t))) {
            urls.push(o.asset.url);
            continue;
          }
          if (sceneObjectEffectiveVisible.get(o.id) !== false) {
            urls.push(o.asset.url);
          }
        }
        prefetchSceneModelAssetUrls(urls);
      }
    }, delays.glb);

    const texId = window.setTimeout(() => {
      const ruleGraphs = getRuleGraphs?.() ?? [];
      const plan = buildRuleCompPrefetchPlan(
        ruleGraphs,
        productModel,
        (stableSelections ?? configState.selections) as Record<string, unknown>,
      );
      // Materials that paint the shared Basic cage (wood / carpaint / straw / …).
      const shellMatIds = new Set<string>();
      if (mobileShellOnly) {
        for (const id of plan.materialIds) shellMatIds.add(id);
        for (const m of libraryMaterials) {
          if (/^(Wood|Carpaint|Straw|Gradient|Metal Mesh|Carbon|Leather|Rubber)/i.test(String(m?.name || ''))) {
            shellMatIds.add(m.id);
            if (m.name) shellMatIds.add(m.name);
          }
        }
      }
      const matsToWarm =
        libraryMaterials.length === 0
          ? []
          : libraryMaterials.filter((m: any) => {
              if (m?.type !== 'graph' || !Array.isArray(m.data?.nodes)) return false;
              if (mobileShellOnly) {
                return shellMatIds.has(m.id) || shellMatIds.has(m.name);
              }
              if (plan.materialIds.size === 0) return true;
              return plan.materialIds.has(m.id) || plan.materialIds.has(m.name);
            });
      for (const m of matsToWarm) {
        try {
          warmMaterialGraphTextureCache(collectMaterialGraphImageUrls(m.data));
        } catch {
          /* ignore */
        }
      }
      // Do not warm materials outside the rule/shell plan — leftover graphs blow Apple GPU RAM.
    }, delays.tex);

    const evalId = window.setTimeout(() => {
      if (mobileShellOnly) return; // texture warm is enough; full eval variants are heavy on iPad
      const ruleGraphs = getRuleGraphs?.() ?? [];
      const plan = buildRuleCompPrefetchPlan(
        ruleGraphs,
        productModel,
        (stableSelections ?? configState.selections) as Record<string, unknown>,
      );
      const attrDefs =
        productModel?.attributes?.map((a: any) => ({
          key: a.key,
          type: a.type,
          defaultValue: a.defaultValue ?? null,
          options: a.options || a.values || [],
        })) ?? [];
      const matsToWarm =
        libraryMaterials.length === 0
          ? []
          : libraryMaterials.filter((m: any) => {
              if (m?.type !== 'graph' || !Array.isArray(m.data?.nodes)) return false;
              if (plan.materialIds.size === 0) return true;
              return plan.materialIds.has(m.id) || plan.materialIds.has(m.name);
            });
      for (const m of matsToWarm) {
        try {
          warmMaterialGraphEvalVariants(
            m.data,
            attrDefs,
            (stableSelections ?? {}) as Record<string, unknown>,
            undefined,
            48,
          );
        } catch {
          /* ignore */
        }
      }
    }, delays.eval);

    return () => {
      window.clearTimeout(glbId);
      window.clearTimeout(texId);
      window.clearTimeout(evalId);
    };
  }, [
    firstRevealReady,
    configState?.derived,
    configState?.selections,
    libraryMaterials,
    productModel,
    sceneData?.objects,
    stableSelections,
    getRuleGraphs,
    sceneObjectEffectiveVisible,
    sceneData,
    deviceInfo.isMobile,
    mobilePinnedMountIds,
    exclusiveFinishMountIds,
  ]);

  const showBlockingLoadOverlay = !blockingLoadUiDismissed;
  const loadPhase: 'assets' | 'finishing' =
    modelsWaveDone && hasGraphMaterials && !firstRevealReady ? 'finishing' : 'assets';
  const revealProgress = !modelsWaveDone
    ? loadProgress
    : firstRevealReady
      ? 100
      : Math.min(99, Math.max(loadProgress, 88));

  const catalogModelUrlsKey = useMemo(
    () => visibleModels.map((m) => m.url).sort().join('\x1e'),
    [visibleModels],
  );

  const diagnosticsAssetStats = useMemo((): AssetLoadStats | null => {
    if (!productModel) return null;
    return {
      sceneObjectCount: sceneData?.objects?.length ?? 0,
      sceneModelsRendered: hasSceneModels ? sceneModelObjects.length : visibleModels.length,
      catalogGlbCount: assets.models.length,
      libraryMaterialCount: libraryMaterials.length,
      productId: productModel.productId,
    };
  }, [
    productModel,
    sceneData?.objects?.length,
    hasSceneModels,
    sceneModelObjects.length,
    visibleModels.length,
    assets.models.length,
    libraryMaterials.length,
  ]);

  const setCameraPreset = useCallback((key: string) => {
    setActivePreset(key);
    const preset = CAMERA_PRESETS.find((p) => p.key === key);
    if (!preset) return;
    const canvas = document.querySelector('[data-viewer] canvas');
    if (!canvas) return;
    const event = new CustomEvent('camera-preset', { detail: preset.position });
    canvas.dispatchEvent(event);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = document.querySelector('[data-viewer]');
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      el.requestFullscreen();
    }
  }, []);

  useEffect(() => {
    setCatalogModelsLoaded(0);
  }, [visibleModels.length]);

  const visibleModelsRef = React.useRef(visibleModels);
  visibleModelsRef.current = visibleModels;

  // Prefetch as soon as first-wave scene URLs are known (and again for catalog-only products).
  useEffect(() => {
    if (hasSceneModels) {
      if (!sceneData?.objects?.length) return;
      prefetchSceneModelAssetUrls(firstWaveGlbUrls(sceneData.objects, ruleActions));
    } else if (blockingLoadUiDismissed) {
      const models = visibleModelsRef.current;
      if (models.length > 0) {
        prefetchSceneModelAssetUrls(models.map((m) => m.url));
      }
    }
  }, [
    blockingLoadUiDismissed,
    hasSceneModels,
    sceneGlbLineupResetKey,
    catalogModelUrlsKey,
    sceneData?.objects,
    ruleActions,
  ]);

  const handleSceneGlbLoaded = useCallback((objectId: string) => {
    setSceneGlbLoadedById((prev) => (prev[objectId] ? prev : { ...prev, [objectId]: true }));
  }, []);

  const handleCatalogModelLoaded = useCallback(() => {
    setCatalogModelsLoaded((prev) => prev + 1);
  }, []);

  const handleAnimationsLoaded = useCallback((clipNames: string[]) => {
    setAnimClips(clipNames);
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  useEffect(() => {
    if (effectiveHideControls) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement || e.target instanceof HTMLTextAreaElement) return;

      switch (e.key) {
        case 'f':
        case 'F':
          e.preventDefault();
          toggleFullscreen();
          return;
        case 'r':
          e.preventDefault();
          if (resetConfiguration) resetConfiguration();
          return;
        case 'R':
          e.preventDefault();
          setAutoRotateMode((prev) => prev === 'off' ? 'rotate' : prev === 'rotate' ? 'rotate-reset' : 'off');
          return;
        case '?':
          e.preventDefault();
          setShowShortcuts((p) => !p);
          return;
        case 'Escape':
          if (showShortcuts) { setShowShortcuts(false); return; }
          if (showShareModal) { setShowShareModal(false); return; }
          if (activeChip) { setActiveChip(null); return; }
          if (showSummary) { setShowSummary(false); return; }
          return;
        case '1': case '2': case '3': case '4': {
          const idx = parseInt(e.key) - 1;
          if (idx < CAMERA_PRESETS.length) {
            e.preventDefault();
            setCameraPreset(CAMERA_PRESETS[idx].key);
          }
          return;
        }
      }

      if (!productModel || !configState) return;
      const comps = productModel.components ?? [];
      if (comps.length === 0) return;

      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        for (const comp of comps) {
          const variants = comp.allowedVariantIds ?? [];
          if (variants.length < 2) continue;
          const currentId = configState.selections[`component:${comp.id}`] as string | undefined;
          const currentIdx = currentId ? variants.indexOf(currentId) : -1;
          let nextIdx: number;
          if (e.key === 'ArrowRight') {
            nextIdx = currentIdx < variants.length - 1 ? currentIdx + 1 : 0;
          } else {
            nextIdx = currentIdx > 0 ? currentIdx - 1 : variants.length - 1;
          }
          selectComponent(comp.id, variants[nextIdx]);
          return;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [effectiveHideControls, productModel, configState, selectComponent, toggleFullscreen, resetConfiguration, showShareModal, showShortcuts, setCameraPreset, activeChip, showSummary]);

  useEffect(() => {
    let touchStartY = 0;
    let touchStartTime = 0;
    let lastTapTime = 0;
    const DOUBLE_TAP_MS = 300;

    function handleTouchStart(e: TouchEvent) {
      touchStartY = e.touches[0].clientY;
      touchStartTime = Date.now();
    }

    function handleTouchEnd(e: TouchEvent) {
      const deltaY = e.changedTouches[0].clientY - touchStartY;
      const elapsed = Date.now() - touchStartTime;

      if (Math.abs(deltaY) < 10 && elapsed < 250) {
        const now = Date.now();
        if (now - lastTapTime < DOUBLE_TAP_MS) {
          resetConfiguration();
          lastTapTime = 0;
          return;
        }
        lastTapTime = now;
      }
    }

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [resetConfiguration]);

  useEffect(() => {
    const apply = () => {
      if (typeof window === 'undefined') return;
      const w = window.innerWidth;
      const coarse =
        typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
      // Match Tailwind lg (1024): tablets in portrait use stacked viewer + docked rail, not overlay on full-width canvas.
      // Coarse pointer + mid width: iPad landscape still gets docked layout so the rail does not crop the 3D view.
      setIsNarrowViewport(w <= 1023 || (coarse && w <= 1366));
    };
    apply();
    window.addEventListener('resize', apply);
    const mq = typeof window.matchMedia === 'function' ? window.matchMedia('(pointer: coarse)') : null;
    mq?.addEventListener('change', apply);
    return () => {
      window.removeEventListener('resize', apply);
      mq?.removeEventListener('change', apply);
    };
  }, []);

  useEffect(() => {
    try {
      if (sessionStorage.getItem('c3d_mobile_cfg_collapsed') === '1') setMobileCfgCollapsed(true);
    } catch { /* ignore */ }
  }, []);

  const handleShare = useCallback(async () => {
    if (!configState) return;
    setShareStatus('saving');
    try {
      const res = await fetch(`${getPlayerApiBase()}/api/configurations/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId,
          selections: configState.selections,
          revision: configState.revision,
        }),
      });
      if (!res.ok) throw new Error('Failed to save');
      const data = await res.json();
      setSavedConfigId(data.id);
      const playerOrigin = getPlayerApiBase() || window.location.origin;
      const url = `${playerOrigin}/?share=${data.shareToken}`;
      setShareUrl(url);
      setShowShareModal(true);
      setShareStatus('copied');
      await navigator.clipboard.writeText(url);
      setTimeout(() => setShareStatus('idle'), 2500);
      fetch(statsEventsUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId, event: 'config_save', payload: { shareToken: data.shareToken } }),
      }).catch(() => {});
    } catch {
      setShareStatus('idle');
    }
  }, [configState, productId]);

  const handleAddAnnotation = useCallback(async () => {
    if (!savedConfigId || !newAnnotation.trim()) return;
    try {
      const res = await fetch(`${getPlayerApiBase()}/api/configurations/saved/${savedConfigId}/annotations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: newAnnotation.trim() }),
      });
      if (!res.ok) throw new Error('Failed to add annotation');
      const annotation = await res.json();
      setAnnotations((prev) => [annotation, ...prev]);
      setNewAnnotation('');
    } catch {}
  }, [savedConfigId, newAnnotation]);

  const handleDeleteAnnotation = useCallback(async (annotationId: string) => {
    try {
      await fetch(`${getPlayerApiBase()}/api/configurations/annotations/${annotationId}`, { method: 'DELETE' });
      setAnnotations((prev) => prev.filter((a) => a.id !== annotationId));
    } catch {}
  }, []);

  const handleSaveNotes = useCallback(async () => {
    if (!savedConfigId) return;
    try {
      await fetch(`${getPlayerApiBase()}/api/configurations/saved/${savedConfigId}/notes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: configNotes }),
      });
    } catch {}
  }, [savedConfigId, configNotes]);

  const resolveSelectionLabel = useCallback((key: string, value: unknown): { label: string; display: string } => {
    if (!productModel) return { label: key, display: String(value) };
    const comps = productModel.components ?? [];
    if (key.startsWith('component:')) {
      const compId = key.replace('component:', '');
      const comp = comps.find((c) => c.id === compId);
      const variantMeta = (comp?.metadata as any)?.variants?.[String(value)];
      return { label: comp?.label || compId, display: variantMeta?.name || String(value) };
    }
    const attr = productModel.attributes.find((a) => a.key === key);
    if (attr) {
      const attrValue = attr.values?.find((v) => v.key === String(value));
      return { label: attr.label, display: attrValue?.label || String(value) };
    }
    return { label: key, display: String(value) };
  }, [productModel]);

  const handleDownloadSummary = useCallback(() => {
    if (!productModel || !configState) return;
    const selections = Object.entries(configState.selections).filter(([, v]) => !Array.isArray(v));
    const lines: string[] = [];
    lines.push('═══════════════════════════════════════');
    lines.push(`  ${productModel.name} — Your configuration`);
    lines.push('═══════════════════════════════════════');
    lines.push('');
    lines.push(`Date: ${new Date().toLocaleString()}`);
    lines.push('');

    if (totalPrice) {
      lines.push(`Total Price: ${totalPrice}`);
      lines.push('');
    }

    lines.push('─── Selections ───');
    lines.push('');
    for (const [key, value] of selections) {
      const resolved = resolveSelectionLabel(key, value);
      lines.push(`  ${resolved.label}: ${resolved.display}`);
    }
    lines.push('');
    lines.push('─── Product Info ───');
    lines.push('');
    lines.push(`  Product ID: ${productModel.id}`);
    if (productModel.description) lines.push(`  Description: ${productModel.description}`);
    lines.push(`  Attributes: ${productModel.attributes?.length ?? 0}`);
    lines.push(`  Components: ${productModel.components?.length ?? 0}`);
    lines.push('');
    lines.push('═══════════════════════════════════════');

    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${productModel.name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}-config-summary.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [productModel, configState, totalPrice, resolveSelectionLabel]);

  const activeSelections = useMemo(() => {
    if (!configState) return [];
    return Object.entries(configState.selections).filter(
      ([, v]) => v != null && v !== '' && v !== undefined,
    );
  }, [configState]);

  const hasSelections = activeSelections.length > 0;

  const componentMetadataMap = useMemo(() => {
    const map: Record<string, Record<string, unknown> | undefined> = {};
    if (!productModel?.components) return map;
    for (const comp of productModel.components) {
      map[comp.id] = comp.metadata as Record<string, unknown> | undefined;
    }
    return map;
  }, [productModel]);

  const handleChipSelect = useCallback((chipId: string | null) => {
    setActiveChip(chipId);
    setShowSummary(false);
  }, []);

  const toggleMobileCfgCollapsed = useCallback(() => {
    setMobileCfgCollapsed((prev) => {
      const next = !prev;
      try {
        sessionStorage.setItem('c3d_mobile_cfg_collapsed', next ? '1' : '0');
      } catch { /* ignore */ }
      return next;
    });
  }, []);

  const handleSummaryOpen = useCallback(() => {
    setShowSummary((prev) => !prev);
    setActiveChip(null);
  }, []);

  const visibleAttributes = useMemo(() => {
    if (!productModel || !configState) return [];
    return productModel.attributes.filter((attr) => {
      const meta = attr.metadata as Record<string, unknown> | undefined;
      if (meta?.hideInPlayer === true) return false;
      const vw = meta?.visibleWhen as { attributeKey?: string; value?: string } | undefined;
      if (!vw || !vw.attributeKey) return true;
      const currentVal = configState.selections[vw.attributeKey];
      if (currentVal === undefined || currentVal === null) return false;
      return String(currentVal) === String(vw.value);
    });
  }, [productModel, configState]);

  /** Player UI: only attribute values marked enabled (omit or `true`); editor keeps full option list. */
  const playerAttributes = useMemo(
    () =>
      visibleAttributes.map((attr) => ({
        ...attr,
        values: attr.values?.filter((v) => v.enabled !== false),
      })),
    [visibleAttributes],
  );

  /** Text input max chars from Text Canvas fontMap for the active Font Type / Dots. */
  const maxLengthByAttr = useMemo(() => {
    if (!configState?.selections || !libraryMaterials.length) return undefined;
    const map = resolveTextAttrMaxCharsFromMaterials(libraryMaterials, configState.selections);
    return Object.keys(map).length ? map : undefined;
  }, [libraryMaterials, configState?.selections]);

  const handleAttributeChange = useCallback(
    (key: string, value: string | number | boolean) => {
      let next: string | number | boolean = value;
      const maxForKey = maxLengthByAttr?.[key];
      if (typeof value === 'string' && maxForKey != null) {
        next = truncateGraphemes(value, maxForKey);
      }
      void setAttribute(key, next);

      // Font Type / Dots change may tighten the limit — clip wired text attrs.
      if (
        (key === 'font_type' || key === 'engraving_dots') &&
        configState?.selections &&
        libraryMaterials.length > 0
      ) {
        const trial = { ...configState.selections, [key]: next };
        const limits = resolveTextAttrMaxCharsFromMaterials(libraryMaterials, trial);
        for (const [textKey, max] of Object.entries(limits)) {
          const eng = configState.selections[textKey];
          if (eng == null || eng === '') continue;
          const clipped = truncateGraphemes(String(eng), max);
          if (clipped !== String(eng)) void setAttribute(textKey, clipped);
        }
      }
    },
    [setAttribute, maxLengthByAttr, configState?.selections, libraryMaterials],
  );

  const components = useMemo(() => {
    const all = productModel?.components ?? [];
    if (Object.keys(componentVariants).length === 0) return all;
    return all.filter((comp) => {
      const variants = componentVariants[comp.id];
      if (!variants) return false;
      return Object.values(variants).some((v) => v !== null);
    });
  }, [productModel?.components, componentVariants]);

  const configuratorExperienceSetting = (sceneData?.metadata as Record<string, unknown> | undefined)
    ?.configuratorExperience as string | undefined;

  const [configuratorExperience, setConfiguratorExperience] = useState<ConfiguratorExperienceMode>('free');
  const [configuratorIntroOpen, setConfiguratorIntroOpen] = useState(false);

  useEffect(() => {
    const raw = configuratorExperienceSetting;
    if (raw === 'guided') {
      setConfiguratorExperience('guided');
      setConfiguratorIntroOpen(false);
      return;
    }
    if (raw === 'free') {
      setConfiguratorExperience('free');
      setConfiguratorIntroOpen(false);
      return;
    }
    if (raw === 'ask') {
      const stored = readStoredExperienceMode();
      if (stored) {
        setConfiguratorExperience(stored);
        setConfiguratorIntroOpen(false);
      } else {
        setConfiguratorIntroOpen(true);
      }
      return;
    }
    setConfiguratorExperience('free');
    setConfiguratorIntroOpen(false);
  }, [productId, sceneData?.id, configuratorExperienceSetting]);

  const guidedStepIds = useMemo(
    () => [
      ...visibleAttributes.map((a) => `attr:${a.key}`),
      ...components.map((c) => `comp:${c.id}`),
    ],
    [visibleAttributes, components],
  );

  const [guidedStepIndex, setGuidedStepIndex] = useState(0);

  useEffect(() => {
    setGuidedStepIndex(0);
  }, [productId, configuratorExperience, guidedStepIds.join('|')]);

  const guidedActiveId =
    configuratorExperience === 'guided' && guidedStepIds.length > 0
      ? guidedStepIds[Math.min(guidedStepIndex, guidedStepIds.length - 1)]!
      : null;

  const onGuidedPrev = useCallback(() => {
    setGuidedStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const onGuidedNext = useCallback(() => {
    setGuidedStepIndex((i) => Math.min(Math.max(0, guidedStepIds.length - 1), i + 1));
  }, [guidedStepIds.length]);

  const memoizedCameraPosition = useMemo(() => {
    const cam = sceneData?.camera as any;
    if (!cam?.position) return undefined;
    return [cam.position.x, cam.position.y, cam.position.z] as [number, number, number];
  }, [sceneData?.camera]);

  const memoizedCameraTarget = useMemo(() => {
    const cam = sceneData?.camera as any;
    if (!cam?.target) return undefined;
    return [cam.target.x, cam.target.y, cam.target.z] as [number, number, number];
  }, [sceneData?.camera]);

  const memoizedRenderAspect = useMemo(() => {
    const cam = sceneData?.camera as any;
    if (!cam) return undefined;
    if (cam.renderWidth > 0 && cam.renderHeight > 0) return cam.renderWidth / cam.renderHeight;
    if (cam.sensorWidth > 0 && cam.sensorHeight > 0) return cam.sensorWidth / cam.sensorHeight;
    const metaCams = (sceneData as any)?.metadata?.cameras;
    const activeCamId = (sceneData as any)?.metadata?.activeCameraId;
    const metaCam = Array.isArray(metaCams)
      ? metaCams.find((c: any) => c.id === activeCamId) || metaCams[0]
      : metaCams;
    if (metaCam) {
      if (metaCam.renderWidth > 0 && metaCam.renderHeight > 0) return metaCam.renderWidth / metaCam.renderHeight;
      if (metaCam.sensorWidth > 0 && metaCam.sensorHeight > 0) return metaCam.sensorWidth / metaCam.sensorHeight;
    }
    return undefined;
  }, [sceneData?.camera, sceneData?.metadata]);

  const memoizedBgImageAdjust = useMemo(() => {
    const bg = (sceneData as any)?.metadata?.background;
    if (!bg) return undefined;
    return {
      brightness: bg.imageBrightness,
      contrast: bg.imageContrast,
      saturation: bg.imageSaturation,
      temperature: bg.imageTemperature,
    };
  }, [sceneData?.metadata]);

  const memoizedPostfx = useMemo(() => {
    const raw = (sceneData as any)?.metadata?.postfx;
    // Match the editor: use the scene's AA mode. Forcing SMAA here made
    // paradis.3kit.com look softer than the MSAA viewport we author in.
    // Keep TAA only when the scene asks for it (glass still ghosts).
    const rawAa = raw?.antialiasing as string | undefined;
    // Prefer SMAA mode when the scene asks for MSAA: composer still runs N× MSAA,
    // plus morphological SMAA cleans gold rims. Pure `msaa` skips that second pass.
    // Never coerce TAA (glass ghosts). Never enable sharpenBlur here (HDR composer path).
    const antialiasing =
      rawAa === 'taa' ? ('taa' as const) : rawAa === 'fxaa' ? ('fxaa' as const) : ('smaa' as const);
    const pfx = {
      smaaPreset: 'ultra' as const,
      enabled: true,
      ...(raw || {}),
      antialiasing,
      outputMsaaSamples: typeof raw?.outputMsaaSamples === 'number' && raw.outputMsaaSamples > 0
        ? Math.max(raw.outputMsaaSamples, 8)
        : 8,
      // Authored bias `1` forces softer mips — looks pixelated/mushy on Retina. Sharp = 0.
      textureLodBias: 0,
      textureMaxAnisotropy: typeof raw?.textureMaxAnisotropy === 'number' ? raw.textureMaxAnisotropy : 16,
      dprMin: typeof raw?.dprMin === 'number' ? raw.dprMin : 1.5,
      dprMax: typeof raw?.dprMax === 'number' ? raw.dprMax : 2.5,
    };
    if (!pfx.ssao?.enabled) return pfx;
    const hasExclude = sceneData?.objects?.some((o: any) => o.metadata?.excludeFromSSAO);
    if (!hasExclude) return pfx;
    return { ...pfx, ssao: { ...pfx.ssao, excludeActive: true } };
  }, [sceneData?.metadata, sceneData?.objects]);

  const mobilePostfx = useMemo(() => {
    if (!deviceInfo.isMobile || !memoizedPostfx) return memoizedPostfx;
    // Tablets: sharpen within the known-good load budget (low-power + fixed DPR + 4× MSAA).
    // Ultra SMAA alone is cheap; high-performance GL / desktop DPR path is what OOM'd load.
    if (deviceInfo.isTablet) {
      return {
        ...memoizedPostfx,
        antialiasing: (memoizedPostfx.antialiasing === 'msaa' ? 'smaa' : memoizedPostfx.antialiasing) || ('smaa' as const),
        outputMsaaSamples: Math.min(Number(memoizedPostfx.outputMsaaSamples) || 4, 6),
        smaaPreset: 'ultra' as const,
        textureMaxAnisotropy: Math.min(Number(memoizedPostfx.textureMaxAnisotropy) || 16, 16),
        textureLodBias: 0,
        dprMin: 2,
        dprMax: 2,
        ssao: memoizedPostfx.ssao ? { ...memoizedPostfx.ssao, enabled: false } : undefined,
      };
    }
    return {
      ...memoizedPostfx,
      antialiasing: 'fxaa' as const,
      outputMsaaSamples: 0,
      smaaPreset: 'medium' as const,
      textureMaxAnisotropy: Math.min(Number(memoizedPostfx.textureMaxAnisotropy) || 4, 4),
      dprMin: 1,
      dprMax: 1.5,
      ssao: memoizedPostfx.ssao ? { ...memoizedPostfx.ssao, enabled: false } : undefined,
    };
  }, [deviceInfo.isMobile, deviceInfo.isTablet, memoizedPostfx]);

  const debugViewerPostfx = useMemo(() => {
    if (!playerDebugEnabled || debugPostfxOn) return mobilePostfx;
    return viewerPostfxAllOff(mobilePostfx);
  }, [playerDebugEnabled, debugPostfxOn, mobilePostfx]);

  /** Hard reset: rules / fades / scene — clears history and re-accumulates (may jitter). */
  const playerPostfxTaaResetKey = useMemo(
    () =>
      `${sceneData?.id ?? ''}|${configState?.revision ?? 0}|${ruleVisibilitySignature}|${[...fadedOutIds].sort().join(',')}`,
    [sceneData?.id, configState?.revision, ruleVisibilitySignature, fadedOutIds],
  );

  /**
   * Soft reset when selections change AND when all deferred graph maps finish.
   * Per-mesh map-ready used to fire dozens of soft resets on prod while CPU bakes
   * landed one-by-one — idle SSAA kept empty frames and left fading ghost trails.
   */
  const [mapsReadyEpoch, setMapsReadyEpoch] = useState(0);
  useEffect(() => {
    if (!firstRevealReady) return;
    let debounce: ReturnType<typeof setTimeout> | null = null;
    const bump = () => setMapsReadyEpoch((n) => n + 1);
    const bumpDebounced = () => {
      if (debounce) clearTimeout(debounce);
      // Defer — graph idle events fire synchronously during GlbModel render.
      debounce = setTimeout(bump, 400);
    };
    document.addEventListener('scene-graph-textures-idle', bumpDebounced);
    document.addEventListener('scene-material-maps-ready', bumpDebounced);
    return () => {
      document.removeEventListener('scene-graph-textures-idle', bumpDebounced);
      document.removeEventListener('scene-material-maps-ready', bumpDebounced);
      if (debounce) clearTimeout(debounce);
    };
  }, [firstRevealReady]);

  const playerPostfxTaaSoftResetKey = useMemo(
    () => `${selectionsKey}|${mapsReadyEpoch}`,
    [selectionsKey, mapsReadyEpoch],
  );

  const viewerCanvasDpr = useMemo(() => {
    if (playerDebugEnabled && debugDprMode !== 'default') {
      if (debugDprMode === '1') return [1, 1] as [number, number];
      return [1, 1.5] as [number, number];
    }
    if (deviceInfo.isTablet) {
      // Integer 2× backing store — fractional DPR softens edges via bilinear downscale.
      return 2;
    }
    if (deviceInfo.isMobile) {
      const phys = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
      return Math.min(1.5, Math.max(1, phys));
    }
    return undefined;
  }, [playerDebugEnabled, debugDprMode, deviceInfo.isMobile, deviceInfo.isTablet]);

  const viewerTransmissionScale = useMemo(() => {
    if (playerDebugEnabled && !debugTransmissionFull) return 0.5;
    if (deviceInfo.isTablet) return 1;
    if (deviceInfo.isMobile) return 0.5;
    // Keep ≤1 at supersampled DPR: Three.js TX RTs used 4× MSAA and blew the GPU.
    // (MSAA is now patched off; still avoid oversized buffers.)
    return 1;
  }, [playerDebugEnabled, debugTransmissionFull, deviceInfo.isMobile, deviceInfo.isTablet]);

  const viewerShadowsEnabled = !playerDebugEnabled || debugShadowsOn;

  const viewerGlOverrides = useMemo(() => {
    if (playerDebugEnabled && debugLowPowerGl) return { powerPreference: 'low-power' as const };
    // All iOS/Android: low-power — high-performance on iPad failed first paint / load.
    if (deviceInfo.isMobile) return { powerPreference: 'low-power' as const };
    return undefined;
  }, [playerDebugEnabled, debugLowPowerGl, deviceInfo.isMobile]);

  const memoizedOrbitTarget = useMemo(() => {
    const cam = sceneData?.camera as any;
    if (!sceneData?.objects?.length) {
      if (cam?.target) return [cam.target.x, cam.target.y, cam.target.z] as [number, number, number];
      return undefined;
    }
    const effVis = buildEffectiveVisibleMap(sceneData.objects);
    const meshes = sceneData.objects.filter((o) => o.asset?.url && effVis.get(o.id) !== false);
    if (!meshes.length) {
      if (cam?.target) return [cam.target.x, cam.target.y, cam.target.z] as [number, number, number];
      return undefined;
    }
    const product = {
      x: meshes.reduce((s, o) => s + (o.transform?.position?.x ?? 0), 0) / meshes.length,
      y: meshes.reduce((s, o) => s + (o.transform?.position?.y ?? 0), 0) / meshes.length,
      z: meshes.reduce((s, o) => s + (o.transform?.position?.z ?? 0), 0) / meshes.length,
    };
    if (cam?.position && cam?.target) {
      const p = cam.position;
      const tgt = cam.target;
      const dx = tgt.x - p.x, dy = tgt.y - p.y, dz = tgt.z - p.z;
      const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (len > 0.0001) {
        const nx = dx / len, ny = dy / len, nz = dz / len;
        const t = (product.x - p.x) * nx + (product.y - p.y) * ny + (product.z - p.z) * nz;
        if (t > 0.1) {
          return [p.x + nx * t, p.y + ny * t, p.z + nz * t] as [number, number, number];
        }
      }
    }
    return [product.x, product.y, product.z] as [number, number, number];
  }, [sceneData?.objects, sceneData?.camera]);

  const cameraMinDistance = useMemo(() => {
    const saved = Number((sceneData?.camera as any)?.minDistance);
    if (Number.isFinite(saved) && saved > 0) return saved;
    if (!memoizedCameraPosition) return undefined;
    const target = memoizedOrbitTarget || memoizedCameraTarget;
    if (!target) return undefined;
    const dx = memoizedCameraPosition[0] - target[0];
    const dy = memoizedCameraPosition[1] - target[1];
    const dz = memoizedCameraPosition[2] - target[2];
    const authoredDistance = Math.sqrt(dx * dx + dy * dy + dz * dz);
    return authoredDistance > 0.0001 ? authoredDistance * 0.6 : undefined;
  }, [sceneData?.camera, memoizedCameraPosition, memoizedOrbitTarget, memoizedCameraTarget]);

  const cameraMaxDistance = useMemo(() => {
    const saved = Number((sceneData?.camera as any)?.maxDistance);
    if (Number.isFinite(saved) && saved > 0) {
      return Math.max(saved, cameraMinDistance ?? 0);
    }
    if (!memoizedCameraPosition) return undefined;
    const target = memoizedOrbitTarget || memoizedCameraTarget;
    if (!target) return undefined;
    const dx = memoizedCameraPosition[0] - target[0];
    const dy = memoizedCameraPosition[1] - target[1];
    const dz = memoizedCameraPosition[2] - target[2];
    const authoredDistance = Math.sqrt(dx * dx + dy * dy + dz * dz);
    return authoredDistance > 0.0001
      ? Math.max(authoredDistance * 4, cameraMinDistance ?? 0)
      : undefined;
  }, [sceneData?.camera, memoizedCameraPosition, memoizedOrbitTarget, memoizedCameraTarget, cameraMinDistance]);

  const mobileRailClearancePx = useMemo(() => {
    if (!isNarrowViewport || effectiveHideControls) return undefined;
    return mobileCfgCollapsed ? MOBILE_CONFIG_COLLAPSED_PX : MOBILE_CONFIG_COLLAPSED_PX + MOBILE_PANEL_BODY_EST_PX;
  }, [isNarrowViewport, effectiveHideControls, mobileCfgCollapsed]);

  // --- Early returns (after all hooks) ---

  if (accessLevel === 'password' && !accessGranted) {
    return (
      <PasswordGate
        productId={productId}
        productName={productModel?.name}
        onGranted={() => setAccessGranted(true)}
      />
    );
  }

  if (loading) {
    return <BootLoader productName={productModel?.name} />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-zinc-50 dark:bg-zinc-950">
        <div className="text-center max-w-sm">
          <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <p className="text-red-500 text-sm mb-4">{error}</p>
          <div className="flex gap-2 justify-center">
            <button
              onClick={initSession}
              className="px-4 py-2 rounded-md bg-zinc-900 text-white text-sm hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-200 transition-colors"
            >
              Retry
            </button>
            <a
              href="/"
              className="px-4 py-2 rounded-md border border-zinc-300 dark:border-zinc-700 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            >
              Back to Products
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (!productModel || !configState) return null;

  return (
    <div
      className="relative flex min-h-0 flex-col overflow-hidden bg-zinc-50 dark:bg-zinc-950 h-[100dvh] max-h-[100dvh] max-lg:h-[100svh] max-lg:max-h-[100svh] pl-[max(0px,env(safe-area-inset-left))] pr-[max(0px,env(safe-area-inset-right))] pt-[max(0px,env(safe-area-inset-top))] pb-[max(0px,env(safe-area-inset-bottom))]"
      role="application"
      aria-label={`${productModel.name} 3D Configurator`}
      style={{ fontFamily: 'var(--c3d-font, inherit)', borderRadius: 'var(--c3d-radius, inherit)' }}
    >
      <SocialMeta
        productName={productModel.name}
        description={productModel.description}
        metadata={productModel.metadata}
      />
      <ThemeProvider metadata={productModel.metadata} />
      <LoadingScreen
        productName={productModel.name}
        metadata={productModel.metadata}
        progress={revealProgress}
        phase={loadPhase}
        visible={showBlockingLoadOverlay}
      />
      {!noViewerChrome && productTourEnabled ? <ProductTour /> : null}
      {!noViewerChrome && <ShortcutHelp />}
      {!noViewerChrome && (
        <PerfOverlay
          modelCount={visibleModels.length}
          loadStartTime={loadStartTime}
          allLoaded={allLoaded}
          sceneStats={sceneStats}
          assetStats={diagnosticsAssetStats}
          textureQualityPreset={textureQualityPreset ?? null}
        />
      )}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {hasSelections && `Configuration: ${activeSelections.filter(([,v]) => !Array.isArray(v)).map(([k, v]) => { const r = resolveSelectionLabel(k, v); return `${r.label}: ${r.display}`; }).join(', ')}`}
        {totalPrice && `. Total: ${totalPrice}`}
      </div>
      {!noViewerChrome && (
        <a href="#config-options" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-white focus:px-4 focus:py-2 focus:rounded-md focus:shadow-lg focus:text-sm">
          Skip to configuration options
        </a>
      )}
      <div className="relative flex min-h-0 flex-1 flex-col lg:absolute lg:inset-0 lg:flex lg:flex-col">
      <div
        className="relative w-full min-h-0 flex-1 max-lg:min-h-[min(11rem,30svh)] lg:h-full"
        data-viewer
        role="region"
        aria-label="3D Viewer"
      >
        {!noViewerChrome && (
          <DimensionOverlay
            productMetadata={productModel?.metadata}
            hideAxisLegend={effectiveChromeMinimal}
            toolbarCluster={
              !embed && !effectiveHideToolbar
                ? (rulerToggle) => (
                    <div className="absolute top-3 right-3 z-50 flex items-center gap-2">
                      {rulerToggle}
                      <div className="flex items-center h-8 rounded-full border border-zinc-900/15 bg-gradient-to-r from-white/85 via-white/95 to-white/85 shadow-md shadow-black/10 backdrop-blur-md dark:border-white/15 dark:from-zinc-900/85 dark:via-zinc-950/95 dark:to-zinc-900/85 dark:shadow-black/40 overflow-hidden">
                        <AutoRotateToolbarButton
                          autoRotateMode={autoRotateMode}
                          onAutoRotateCycle={() =>
                            setAutoRotateMode((m) => (m === 'off' ? 'rotate' : m === 'rotate' ? 'rotate-reset' : 'off'))
                          }
                          darkGlass
                          toolbarStrip
                        />
                        <span className="w-px h-4 shrink-0 bg-zinc-900/10 dark:bg-white/15" aria-hidden />
                        <button
                          onClick={() => setShowShortcuts(true)}
                          className="h-8 w-8 flex items-center justify-center text-zinc-700 hover:bg-zinc-900/[0.06] hover:text-zinc-900 transition-all duration-150 text-xs font-mono font-bold dark:text-zinc-200 dark:hover:bg-white/10 dark:hover:text-white"
                          title="Help & Stats (?)"
                        >
                          ?
                        </button>
                        <span className="w-px h-4 shrink-0 bg-zinc-900/10 dark:bg-white/15" />
                        <button
                          onClick={() => {
                            initSession();
                          }}
                          className="h-8 w-8 flex items-center justify-center text-zinc-700 hover:bg-zinc-900/[0.06] hover:text-zinc-900 transition-all duration-150 dark:text-zinc-200 dark:hover:bg-white/10 dark:hover:text-white"
                          title="Reload session"
                        >
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182M2.985 19.644l3.181-3.182"
                            />
                          </svg>
                        </button>
                        <span className="w-px h-4 shrink-0 bg-zinc-900/10 dark:bg-white/15" />
                        <button
                          onClick={async () => {
                            const canvas = document.querySelector('[data-viewer] canvas') as HTMLCanvasElement | null;
                            if (!canvas) return;

                            const offscreen = document.createElement('canvas');
                            offscreen.width = canvas.width;
                            offscreen.height = canvas.height;
                            const ctx = offscreen.getContext('2d');
                            if (!ctx) return;
                            ctx.drawImage(canvas, 0, 0);

                            if (activeSelections.length > 0) {
                              const padding = 12;
                              const lineHeight = 16;
                              const lines = activeSelections
                                .filter(([, v]) => !Array.isArray(v))
                                .map(([k, v]) => {
                                  const r = resolveSelectionLabel(k, v);
                                  return `${r.label}: ${r.display}`;
                                })
                                .slice(0, 6);

                              if (lines.length > 0) {
                                const boxH = lines.length * lineHeight + padding * 2;
                                ctx.fillStyle = 'rgba(0,0,0,0.6)';
                                ctx.beginPath();
                                ctx.roundRect(padding, offscreen.height - boxH - padding, 280, boxH, 8);
                                ctx.fill();
                                ctx.font = '12px Inter, system-ui, sans-serif';
                                ctx.fillStyle = '#fff';
                                lines.forEach((line, i) => {
                                  ctx.fillText(
                                    line,
                                    padding + 10,
                                    offscreen.height - boxH - padding + padding + i * lineHeight + 12,
                                  );
                                });
                              }
                            }

                            const dataUrl = offscreen.toDataURL('image/png');

                            try {
                              const blob = await (await fetch(dataUrl)).blob();
                              await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
                            } catch {}

                            const link = document.createElement('a');
                            link.download = `config3d-${productModel?.name?.replace(/\s+/g, '_') || 'config'}-${Date.now()}.png`;
                            link.href = dataUrl;
                            link.click();
                          }}
                          className="h-8 w-8 flex items-center justify-center text-zinc-700 hover:bg-zinc-900/[0.06] hover:text-zinc-900 transition-all duration-150 dark:text-zinc-200 dark:hover:bg-white/10 dark:hover:text-white"
                          title="Screenshot"
                        >
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                            />
                          </svg>
                        </button>
                        <span className="w-px h-4 shrink-0 bg-zinc-900/10 dark:bg-white/15" />
                        <button
                          onClick={() => setShowComparison(true)}
                          className="h-8 w-8 flex items-center justify-center text-zinc-700 hover:bg-zinc-900/[0.06] hover:text-zinc-900 transition-all duration-150 dark:text-zinc-200 dark:hover:bg-white/10 dark:hover:text-white"
                          title="Compare"
                        >
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"
                            />
                          </svg>
                        </button>
                        <span className="w-px h-4 shrink-0 bg-zinc-900/10 dark:bg-white/15" />
                        <button
                          onClick={toggleFullscreen}
                          className="h-8 w-8 flex items-center justify-center text-zinc-700 hover:bg-zinc-900/[0.06] hover:text-zinc-900 transition-all duration-150 dark:text-zinc-200 dark:hover:bg-white/10 dark:hover:text-white"
                          title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                        >
                          {isFullscreen ? (
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25"
                              />
                            </svg>
                          ) : (
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                              />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  )
                : undefined
            }
          />
        )}
        {!noViewerChrome && <HotspotOverlay productMetadata={productModel?.metadata} />}
        {!noViewerChrome && <MeasurementTool productMetadata={productModel?.metadata} />}
        <div
          className="absolute inset-0 transition-[opacity,transform] ease-out"
          style={{
            opacity: viewerFadeIn ? 1 : 0,
            transform: viewerFadeIn ? 'scale(1)' : 'scale(1.012)',
            transitionDuration: '850ms',
            // Keep the canvas mounting/warming under the loader; only hide visually.
            visibility: firstRevealReady || showBlockingLoadOverlay ? 'visible' : 'hidden',
            pointerEvents: viewerFadeIn ? 'auto' : 'none',
          }}
        >
        <ViewerCanvas
          ref={viewerRef}
          backgroundColor={isFullscreen ? '#18181b' : dark ? '#09090b' : '#fafafa'}
          environmentPreset={envPreset}
          hdrUrl={sceneData?.environment?.hdrUrl || undefined}
          autoRotateMode={autoRotateMode}
          autoRotateSpeed={4}
          introSpinEnabled={introEffect === 'orbit'}
          introSpin={viewerFadeIn && introEffect === 'orbit'}
          introSpinKey={blockingLoadResetKey}
          envIntensity={sceneData?.environment?.intensity}
          envBackgroundBlur={sceneData?.environment?.backgroundBlur}
          envShowBackground={sceneData?.environment?.showBackground}
          envLinkToCamera={sceneData?.environment?.linkToCamera}
          envRotationY={(sceneData?.environment as any)?.rotationY}
          envFlipH={(sceneData?.environment as any)?.flipH}
          envFlipV={(sceneData?.environment as any)?.flipV}
          bgMode={(sceneData as any)?.metadata?.background?.mode}
          bgSolidColor={(sceneData as any)?.metadata?.background?.solidColor}
          bgGradient={(sceneData as any)?.metadata?.background?.gradient}
          bgImageUrl={(sceneData as any)?.metadata?.background?.imageUrl}
          bgImageFit={(sceneData as any)?.metadata?.background?.imageFit}
          bgImageAdjust={memoizedBgImageAdjust}
          postfx={debugViewerPostfx}
          postfxTaaResetKey={playerPostfxTaaResetKey}
          postfxTaaSoftResetKey={playerPostfxTaaSoftResetKey}
          shadows={viewerShadowsEnabled}
          dpr={viewerCanvasDpr}
          transmissionResolutionScale={viewerTransmissionScale}
          glOverrides={viewerGlOverrides}
          onSceneStats={setSceneStats}
          cameraPosition={memoizedCameraPosition}
          cameraTarget={memoizedCameraTarget}
          cameraFov={(sceneData?.camera as any)?.fov || undefined}
          cameraNear={(sceneData?.camera as any)?.near || undefined}
          cameraFar={(sceneData?.camera as any)?.far || undefined}
          cameraLensShiftX={(sceneData?.camera as any)?.lensShiftX || undefined}
          cameraLensShiftY={(sceneData?.camera as any)?.lensShiftY || undefined}
          renderAspect={memoizedRenderAspect}
          orbitTarget={memoizedOrbitTarget}
          cameraMinDistance={cameraMinDistance}
          cameraMaxDistance={cameraMaxDistance}
        >
          <GlobalSplatProvider screenMaskUrl={(sceneData as any)?.metadata?.screenCutoutMaskUrl || undefined}>
          {hasSceneModels ? (
            <>
              {(() => {
                const renderModel = (obj: any) => {
                  const mods = mergeModifiersWithRuleMaps(obj, modifierRuleEnableMap, modifierRuleParamMap, {
                    // Authored radials are 8; phones keep a slight headroom cap, tablets get full count.
                    mobileArrayCap: deviceInfo.isMobile ? (deviceInfo.isTablet ? 8 : 6) : undefined,
                  }) as any[] | undefined;
                  const hasMods = mods && mods.length > 0;
                  const radialMod = mods?.find((m: any) => m.type === 'arrayRadial' && m.params?.centerObjectId);
                  const centerGetter = radialMod ? () => {
                    const cId = (radialMod as any).params.centerObjectId as string;
                    const cObj = sceneData?.objects.find((o) => o.id === cId);
                    if (!cObj) return null;
                    return { x: cObj.transform.position.x, y: cObj.transform.position.y, z: cObj.transform.position.z };
                  } : undefined;
                  const objRuleVisible = isRuleVisible(obj);
                  if (!sceneRuleDrawableShouldMount(obj)) return null;
                  const isSplatCutout = !!(obj.metadata as any)?.isCutout;
                  const stayOnTop = !!(obj.metadata as any)?.stayOnTop;
                  // No Basic-cage bridge while exclusive finishes load — that showed carpaint
                  // during rubber decode and could stick as the wrong end state.
                  const objShowVisible = objRuleVisible && !isExclusiveFinishRevealHeld(obj.id);
                  const glb = (
                    <>
                      <GlbModel
                        url={obj.asset!.url}
                        position={[obj.transform.position.x, obj.transform.position.y, obj.transform.position.z]}
                        rotation={[obj.transform.rotation.x, obj.transform.rotation.y, obj.transform.rotation.z]}
                        scale={[obj.transform.scale.x, obj.transform.scale.y, obj.transform.scale.z]}
                        materialOverrides={materialOverrides}
                        perMeshOverrides={(obj.metadata as any)?.materialOverrides}
                        materialGraphOverrides={materialGraphOverridesMap[obj.id] ?? null}
                        meshVisibility={(obj.metadata as any)?.meshVisibility}
                        excludeFromSSAO={!!(obj.metadata as any)?.excludeFromSSAO}
                        excludeFromRefraction={!!(obj.metadata as any)?.excludeFromRefraction}
                        depthOffset={(obj.metadata as any)?.depthOffset ?? 0}
                        stayOnTop={stayOnTop}
                        animation={animState}
                        onAnimationsLoaded={handleAnimationsLoaded}
                        onLoaded={() => handleSceneGlbLoaded(obj.id)}
                        holdUntilReady
                        preferProgressiveMaterials={deviceInfo.isMobile && !exclusiveFinishMountIds?.has(obj.id)}
                        materialsGateReady={!!configState?.derived}
                        onContentReady={() => handleModelContentReady(obj.id)}
                        transitionDuration={materialTransitionDuration}
                        opacityTransitionDuration={visibilityTransitionDuration}
                        transitionStartedAt={sharedTransitionStartedAt}
                        visible={objShowVisible && !isSplatCutout}
                        opacityOverride={1}
                      />
                      {(isSplatCutout || stayOnTop) && (
                        <SplatCutoutObject
                          id={`cutout-${obj.id}`}
                          type="mesh"
                          url={obj.asset!.url}
                          cutoutTextureUrl={(obj.metadata as any)?.cutoutTextureUrl}
                          mode={isSplatCutout ? 'depth' : 'screen'}
                          position={[obj.transform.position.x, obj.transform.position.y, obj.transform.position.z]}
                          rotation={[obj.transform.rotation.x, obj.transform.rotation.y, obj.transform.rotation.z]}
                          scale={[obj.transform.scale.x, obj.transform.scale.y, obj.transform.scale.z]}
                          visible={objShowVisible}
                        />
                      )}
                    </>
                  );
                  return (
                    <ModelErrorBoundary key={obj.id} resetKey={selectionsKey}>
                      {hasMods ? (
                        <ModifiedMesh
                          modifiers={mods}
                          stayOnTop={stayOnTop}
                          getCenterPosition={centerGetter}
                          visible={objShowVisible && !isSplatCutout}
                        >
                          {glb}
                        </ModifiedMesh>
                      ) : (
                        glb
                      )}
                    </ModelErrorBoundary>
                  );
                };

                const renderGsplat = (obj: any) => {
                  const objRuleVisible = isRuleVisible(obj);
                  if (!sceneRuleDrawableShouldMount(obj)) return null;
                  return (
                    <GsplatObject
                      key={obj.id}
                      id={obj.id}
                      url={(obj.metadata as any)?.viewSplatUrl || obj.asset!.url}
                      position={[obj.transform.position.x, obj.transform.position.y, obj.transform.position.z]}
                      rotation={[obj.transform.rotation.x, obj.transform.rotation.y, obj.transform.rotation.z]}
                      scale={[obj.transform.scale.x, obj.transform.scale.y, obj.transform.scale.z]}
                      visible={objRuleVisible && firstRevealReady}
                      onLoaded={() => {
                        handleSceneGlbLoaded(obj.id);
                        handleModelContentReady(obj.id);
                      }}
                    />
                  );
                };

                const renderPrimitive = (obj: any) => {
                  const primRuleVisible = isRuleVisible(obj);
                  if (!sceneRuleDrawableShouldMount(obj as SceneObjectData)) return null;
                  const shape = (obj.metadata as any)?.primitiveShape as string;
                  const mat = (obj.metadata as any)?.materialConfig as any;
                  const mods = mergeModifiersWithRuleMaps(obj, modifierRuleEnableMap, modifierRuleParamMap, {
                    mobileArrayCap: deviceInfo.isMobile ? (deviceInfo.isTablet ? 8 : 6) : undefined,
                  }) as any[] | undefined;
                  const hasMods = mods && mods.length > 0;
                  const radialMod = mods?.find((m: any) => m.type === 'arrayRadial' && m.params?.centerObjectId);
                  const centerGetter = radialMod ? () => {
                    const cId = (radialMod as any).params.centerObjectId as string;
                    const cObj = sceneData?.objects.find((o) => o.id === cId);
                    if (!cObj) return null;
                    return { x: cObj.transform.position.x, y: cObj.transform.position.y, z: cObj.transform.position.z };
                  } : undefined;
                  const graphOv = materialGraphOverridesMap[obj.id];
                  const gOv = graphOv?.['*'] ?? (graphOv ? Object.values(graphOv)[0] : null);
                  const baseOpacity = gOv?.opacity ?? mat?.opacity ?? 1;
                  const isSplatCutout = !!(obj.metadata as any)?.isCutout;
                  const stayOnTop = !!(obj.metadata as any)?.stayOnTop;
                  const effectiveOpacity = primRuleVisible && !isSplatCutout ? baseOpacity : 0;
                  const primGroup = (
                    <group
                      position={[obj.transform.position.x, obj.transform.position.y, obj.transform.position.z]}
                      rotation={[obj.transform.rotation.x, obj.transform.rotation.y, obj.transform.rotation.z]}
                      scale={[obj.transform.scale.x, obj.transform.scale.y, obj.transform.scale.z]}
                      visible={firstRevealReady && !isSplatCutout && primRuleVisible}
                      renderOrder={stayOnTop ? 20000 : 0}
                    >
                      <mesh
                        castShadow
                        receiveShadow
                        renderOrder={stayOnTop ? 20000 : 0}
                        userData={{ treatAsOpaque: !(obj.metadata as any)?.excludeFromSSAO }}
                      >
                        {(() => {
                          const g = ((obj.metadata as any)?.primitiveGeo || {}) as Record<string, any>;
                          switch (shape) {
                            case 'box': return <boxGeometry args={[g.width ?? 1, g.height ?? 1, g.depth ?? 1, g.widthSegments ?? 1, g.heightSegments ?? 1, g.depthSegments ?? 1]} />;
                            case 'sphere': return <sphereGeometry args={[g.radius ?? 0.5, g.sphereWidthSegs ?? 32, g.sphereHeightSegs ?? 32]} />;
                            case 'cylinder': return <cylinderGeometry args={[g.radiusTop ?? 0.5, g.radiusBottom ?? 0.5, g.cylinderHeight ?? 1, g.radialSegments ?? 32, g.cylinderHeightSegs ?? 1, g.openEnded ?? false]} />;
                            case 'plane': return <planeGeometry args={[g.planeWidth ?? 1, g.planeHeight ?? 1, g.planeWidthSegs ?? 1, g.planeHeightSegs ?? 1]} />;
                            case 'cone': return <coneGeometry args={[g.coneRadius ?? 0.5, g.coneHeight ?? 1, g.coneRadialSegs ?? 32, g.coneHeightSegs ?? 1, g.coneOpenEnded ?? false]} />;
                            case 'torus': return <torusGeometry args={[g.torusRadius ?? 0.4, g.tube ?? 0.15, g.torusRadialSegs ?? 16, g.tubularSegments ?? 48, (g.arc ?? 360) * Math.PI / 180]} />;
                            default: return <boxGeometry args={[1, 1, 1]} />;
                          }
                        })()}
                        <PrimitiveMaterial
                          overrides={gOv ? { ...gOv, opacity: effectiveOpacity } : { opacity: effectiveOpacity }}
                          fallback={mat}
                          depthOffset={(obj.metadata as any)?.depthOffset ?? 0}
                          stayOnTop={stayOnTop}
                        />
                      </mesh>
                    </group>
                  );
                  const cutout = (isSplatCutout || stayOnTop) ? (
                    <SplatCutoutObject
                      id={`cutout-${obj.id}`}
                      type="primitive"
                      primitiveShape={shape}
                      primitiveGeo={(obj.metadata as any)?.primitiveGeo || {}}
                      cutoutTextureUrl={(obj.metadata as any)?.cutoutTextureUrl}
                      mode={isSplatCutout ? 'depth' : 'screen'}
                      position={[obj.transform.position.x, obj.transform.position.y, obj.transform.position.z]}
                      rotation={[obj.transform.rotation.x, obj.transform.rotation.y, obj.transform.rotation.z]}
                      scale={[obj.transform.scale.x, obj.transform.scale.y, obj.transform.scale.z]}
                      visible={primRuleVisible}
                    />
                  ) : null;
                  return hasMods ? (
                    <React.Fragment key={obj.id}>
                      <ModifiedMesh
                        modifiers={mods}
                        stayOnTop={stayOnTop}
                        getCenterPosition={centerGetter}
                        visible={primRuleVisible && !isSplatCutout}
                      >
                        {primGroup}
                      </ModifiedMesh>
                      {cutout}
                    </React.Fragment>
                  ) : (
                    <React.Fragment key={obj.id}>{primGroup}{cutout}</React.Fragment>
                  );
                };

                const renderLight = (obj: any) => {
                  const lc = (obj.metadata as any)?.lightConfig;
                  if (!lc || lc.enabled === false) return null;
                  const col = lc.color ?? '#ffffff';
                  const int = lc.intensity ?? 1;
                  const pos: [number, number, number] = [obj.transform.position.x, obj.transform.position.y, obj.transform.position.z];
                  const rot: [number, number, number] = [obj.transform.rotation.x, obj.transform.rotation.y, obj.transform.rotation.z];
                  return (
                    <group key={obj.id} position={pos} rotation={rot}>
                      {lc.lightType === 'point' && (
                        <pointLight color={col} intensity={int} castShadow={lc.castShadow} distance={lc.distance ?? 0} decay={lc.decay ?? 2} />
                      )}
                      {lc.lightType === 'spot' && (
                        <spotLight color={col} intensity={int} castShadow={lc.castShadow} distance={lc.distance ?? 0} decay={lc.decay ?? 2} angle={lc.angle ?? Math.PI / 6} penumbra={lc.penumbra ?? 0.5} />
                      )}
                      {lc.lightType === 'directional' && (
                        <directionalLight color={col} intensity={int} castShadow={lc.castShadow} />
                      )}
                      {lc.lightType === 'ambient' && (
                        <ambientLight color={col} intensity={int} />
                      )}
                      {lc.lightType === 'area' && (
                        <rectAreaLight color={col} intensity={int} width={lc.width ?? 2} height={lc.height ?? 2} />
                      )}
                    </group>
                  );
                };

                const taggedModels = sceneModelObjects.filter(isObjTagged);
                const nonTaggedModels = sceneModelObjects.filter((o) => !isObjTagged(o));
                const taggedGsplats = sceneGsplatObjects.filter(isObjTagged);
                const nonTaggedGsplats = sceneGsplatObjects.filter((o) => !isObjTagged(o));
                const taggedPrims = scenePrimitives.filter(isObjTagged);
                const nonTaggedPrims = scenePrimitives.filter((o) => !isObjTagged(o));
                const taggedLights = sceneLights.filter(isObjTagged);
                const nonTaggedLights = sceneLights.filter((o) => !isObjTagged(o));

                return (
                  <>
                    {nonTaggedModels.map(renderModel)}
                    {nonTaggedGsplats.map(renderGsplat)}
                    {nonTaggedPrims.map(renderPrimitive)}
                    {nonTaggedLights.map(renderLight)}
                    <ObjectRotationGroup enabled={envLinkToCamera && !!envRotationTagId}>
                      {taggedModels.map(renderModel)}
                      {taggedGsplats.map(renderGsplat)}
                      {taggedPrims.map(renderPrimitive)}
                      {taggedLights.map(renderLight)}
                    </ObjectRotationGroup>
                  </>
                );
              })()}
            </>
          ) : (
            <>
              {visibleModels.map((m) => (
                <ModelErrorBoundary key={m.id} resetKey={selectionsKey}>
                  <GlbModel
                    url={m.url}
                    materialOverrides={materialOverrides}
                    animation={animState}
                    onAnimationsLoaded={handleAnimationsLoaded}
                    onLoaded={handleCatalogModelLoaded}
                    holdUntilReady
                    preferProgressiveMaterials={deviceInfo.isMobile}
                    materialsGateReady={!!configState?.derived}
                    onContentReady={() => handleModelContentReady(m.id)}
                    transitionDuration={materialTransitionDuration}
                    transitionStartedAt={sharedTransitionStartedAt}
                  />
                </ModelErrorBoundary>
              ))}
            </>
          )}
          {blockingLoadUiDismissed && !noViewerChrome && ((productModel.metadata as any)?.hotspots as any[] || []).map((h: any) => (
            <Hotspot key={h.id} hotspot={h} dark={dark} />
          ))}
          {!hasSceneModels && sceneLights.map((obj) => {
            const lc = (obj.metadata as any)?.lightConfig;
            if (!lc || lc.enabled === false) return null;
            const col = lc.color ?? '#ffffff';
            const int = lc.intensity ?? 1;
            const pos: [number, number, number] = [obj.transform.position.x, obj.transform.position.y, obj.transform.position.z];
            const rot: [number, number, number] = [obj.transform.rotation.x, obj.transform.rotation.y, obj.transform.rotation.z];
            return (
              <group key={obj.id} position={pos} rotation={rot}>
                {lc.lightType === 'point' && (
                  <pointLight color={col} intensity={int} castShadow={lc.castShadow} distance={lc.distance ?? 0} decay={lc.decay ?? 2} />
                )}
                {lc.lightType === 'spot' && (
                  <spotLight color={col} intensity={int} castShadow={lc.castShadow} distance={lc.distance ?? 0} decay={lc.decay ?? 2} angle={lc.angle ?? Math.PI / 6} penumbra={lc.penumbra ?? 0.5} />
                )}
                {lc.lightType === 'directional' && (
                  <directionalLight color={col} intensity={int} castShadow={lc.castShadow} />
                )}
                {lc.lightType === 'ambient' && (
                  <ambientLight color={col} intensity={int} />
                )}
                {lc.lightType === 'area' && (
                  <rectAreaLight color={col} intensity={int} width={lc.width ?? 2} height={lc.height ?? 2} />
                )}
              </group>
            );
          })}
          </GlobalSplatProvider>
        </ViewerCanvas>
        </div>
        {playerDebugEnabled && (
          <PlayerDebugPanel
            sceneStats={sceneStats}
            dprMode={debugDprMode}
            onDprMode={setDebugDprMode}
            shadowsOn={debugShadowsOn}
            onShadowsOn={setDebugShadowsOn}
            postfxOn={debugPostfxOn}
            onPostfxOn={setDebugPostfxOn}
            transmissionFull={debugTransmissionFull}
            onTransmissionFull={setDebugTransmissionFull}
            texMode={debugTexMode}
            onTexMode={setDebugTexMode}
            lowPowerGl={debugLowPowerGl}
            onLowPowerGl={setDebugLowPowerGl}
          />
        )}
        {!(sceneData?.camera && (sceneData.camera as any).position) && !noViewerChrome && (
          <div
            className={`absolute left-3 z-10 flex items-center gap-1.5 ${showProductHeader ? 'top-14' : 'top-3'}`}
          >
            <div
              className="flex gap-0.5 bg-black/25 dark:bg-white/10 backdrop-blur-md rounded-full border border-white/10 p-0.5"
              role="toolbar"
              aria-label="Camera presets"
            >
              {CAMERA_PRESETS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setCameraPreset(p.key)}
                  aria-label={`${p.label} camera view`}
                  aria-pressed={activePreset === p.key}
                  className={`px-2.5 py-1 text-[11px] font-medium rounded-full transition-all duration-200 ${
                    activePreset === p.key
                      ? 'bg-white text-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 shadow-sm'
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {visibleModels.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-zinc-400 text-sm">No 3D model available for this product.</p>
          </div>
        )}
        {animClips.length > 0 && !noViewerChrome && (
          <div className="absolute bottom-20 left-3 z-10 flex items-center gap-2 bg-white/80 dark:bg-zinc-800/80 backdrop-blur-sm rounded-md border border-zinc-200 dark:border-zinc-700 px-2 py-1.5">
            <button
              onClick={() => setAnimState((s) => ({ ...s, playing: !s.playing }))}
              className="p-1 text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white"
              title={animState.playing ? 'Pause' : 'Play'}
            >
              {animState.playing ? (
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>
              ) : (
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><polygon points="5,3 19,12 5,21" /></svg>
              )}
            </button>
            <select
              value={animState.speed ?? 1}
              onChange={(e) => setAnimState((s) => ({ ...s, speed: Number(e.target.value) }))}
              className="text-[11px] bg-transparent border-none text-zinc-600 dark:text-zinc-300 outline-none cursor-pointer"
            >
              <option value="0.25">0.25x</option>
              <option value="0.5">0.5x</option>
              <option value="1">1x</option>
              <option value="1.5">1.5x</option>
              <option value="2">2x</option>
            </select>
            {animClips.length > 1 && (
              <select
                value={animState.clipName || animClips[0]}
                onChange={(e) => setAnimState((s) => ({ ...s, clipName: e.target.value }))}
                className="text-[11px] bg-transparent border-none text-zinc-600 dark:text-zinc-300 outline-none cursor-pointer max-w-[100px] truncate"
              >
                {animClips.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            )}
          </div>
        )}
        {embed && (
          <>
          {!effectiveHideToolbar && (
          <div className="absolute right-3 top-3 z-10">
            <AutoRotateToolbarButton
              autoRotateMode={autoRotateMode}
              onAutoRotateCycle={() => setAutoRotateMode((m) => (m === 'off' ? 'rotate' : m === 'rotate' ? 'rotate-reset' : 'off'))}
              darkGlass
            />
          </div>
          )}
          {!noViewerChrome && (
          <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5">
          <button
            onClick={toggleDark}
            className="p-2 rounded-md bg-white/80 dark:bg-zinc-800/80 backdrop-blur-sm border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-white dark:hover:bg-zinc-800 transition-colors"
            title={dark ? 'Light mode' : 'Dark mode'}
          >
            {dark ? (
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
              </svg>
            ) : (
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
              </svg>
            )}
          </button>
          </div>
          )}
          {!noViewerChrome && (
          <div className="absolute bottom-20 right-16 text-[10px] text-zinc-400/50 pointer-events-none select-none">
            Threekit
          </div>
          )}
          </>
        )}
        {!embed && !effectiveHideControls && !effectiveHideHeader && (
          <div className="absolute top-3 left-3 z-50">
            <div className="flex h-8 max-w-[min(100vw-1.5rem,28rem)] items-center gap-0 overflow-hidden rounded-full border border-zinc-900/15 bg-gradient-to-r from-white/85 via-white/95 to-white/85 px-2 shadow-md shadow-black/10 backdrop-blur-md dark:border-white/15 dark:from-zinc-900/85 dark:via-zinc-950/95 dark:to-zinc-900/85 dark:shadow-black/40 sm:px-3">
              <h2 className="min-w-0 shrink truncate px-1.5 text-[11px] font-semibold leading-none text-zinc-800 dark:text-zinc-100 max-w-[8.5rem] sm:max-w-[10.5rem]">
                {(productModel.metadata as any)?.name || productModel.productId}
              </h2>
              <span className="w-px h-4 shrink-0 bg-zinc-900/10 dark:bg-white/15" aria-hidden />
              <span className="shrink-0 px-1.5 text-[9px] leading-none text-zinc-600 dark:text-zinc-400 whitespace-nowrap">
                Rev {configState.revision} · {configState.status.valid ? '✓' : '✗'}
              </span>
              <select
                value={locale}
                onChange={(e) => handleLocaleChange(e.target.value)}
                className="h-6 shrink-0 rounded border border-zinc-900/10 bg-transparent px-1 text-[9px] text-zinc-700 dark:border-white/10 dark:text-zinc-300"
                title="Language"
              >
                {getSupportedLocales().map((l) => (
                  <option key={l} value={l}>{l.toUpperCase()}</option>
                ))}
              </select>
              <span className="w-px h-4 shrink-0 bg-zinc-900/10 dark:bg-white/15" aria-hidden />
              <button
                type="button"
                onClick={toggleDark}
                className="flex h-8 w-8 shrink-0 items-center justify-center text-zinc-700 transition-colors duration-150 hover:bg-zinc-900/[0.06] hover:text-zinc-900 dark:text-zinc-200 dark:hover:bg-white/10 dark:hover:text-white"
                title={dark ? 'Light mode' : 'Dark mode'}
              >
                {dark ? (
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
                  </svg>
                ) : (
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
                  </svg>
                )}
              </button>
              <span className="w-px h-4 shrink-0 bg-zinc-900/10 dark:bg-white/15" aria-hidden />
              <a
                href="/"
                className="shrink-0 px-1.5 text-[9px] leading-none text-zinc-700 transition-colors duration-150 hover:text-zinc-900 whitespace-nowrap dark:text-zinc-200 dark:hover:text-white"
              >
                ← Products
              </a>
            </div>
          </div>
        )}

        {configState.status.issues.length > 0 && !noViewerChrome && (
          <div className={`absolute left-3 z-10 space-y-1 max-w-xs ${showProductHeader ? 'top-24' : 'top-14'}`}>
            {configState.status.issues.map((issue, i) => (
              <div
                key={i}
                className={`text-xs px-2.5 py-1.5 rounded-lg backdrop-blur-sm ${
                  issue.severity === 'error' ? 'bg-red-50/90 text-red-700 dark:bg-red-900/40 dark:text-red-300' :
                  issue.severity === 'warning' ? 'bg-yellow-50/90 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' :
                  'bg-blue-50/90 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                }`}
              >
                {issue.message}
              </div>
            ))}
          </div>
        )}
      </div>

      {!effectiveHideControls && (
        <div
          id="config-rail"
          className={`pointer-events-none z-40 flex shrink-0 flex-col max-lg:w-full lg:absolute ${configRailTopClass} lg:bottom-4 lg:right-[max(0.75rem,env(safe-area-inset-right))] lg:left-auto lg:w-[min(20rem,calc(100vw-1.25rem-env(safe-area-inset-left)-env(safe-area-inset-right)))] lg:max-w-[min(92vw,calc(100vw-1.5rem-env(safe-area-inset-left)-env(safe-area-inset-right)))]`}
        >
          <div
            className={`pointer-events-none flex min-h-0 flex-col overflow-hidden max-lg:w-full lg:pointer-events-auto lg:h-full lg:min-h-0 ${
              isNarrowViewport ? 'max-lg:transition-[max-height] max-lg:duration-300 max-lg:ease-[cubic-bezier(0.32,0.72,0,1)]' : ''
            } ${
              isNarrowViewport && !mobileCfgCollapsed
                ? 'max-lg:flex max-lg:flex-col max-lg:min-h-0 max-lg:max-h-[min(45svh,420px)]'
                : ''
            }`}
            style={
              isNarrowViewport && mobileCfgCollapsed
                ? { height: MOBILE_CONFIG_COLLAPSED_PX }
                : undefined
            }
          >
            <div className="pointer-events-auto flex min-h-0 flex-1 flex-col overflow-hidden max-lg:px-2 max-lg:pb-2 lg:h-full lg:min-h-0">
              <ConfigOptionsPanel
                attributes={playerAttributes}
                components={components}
                selections={configState.selections}
                expandedId={activeChip}
                onExpandedChange={handleChipSelect}
                onAttributeChange={handleAttributeChange}
                onComponentSelect={selectComponent}
                onSummaryOpen={handleSummaryOpen}
                totalPrice={totalPrice}
                hasSelections={hasSelections}
                componentMetadata={componentMetadataMap}
                disabledOptionsByAttr={disabledOptionsByAttr}
                maxLengthByAttr={maxLengthByAttr}
                mobileDocked={isNarrowViewport}
                mobileCollapsed={mobileCfgCollapsed}
                onMobileCollapseToggle={toggleMobileCfgCollapsed}
                desktopCollapsed={desktopCfgCollapsed}
                onDesktopCollapseToggle={toggleDesktopCfgCollapsed}
                experienceMode={configuratorExperience}
                guidedActiveId={guidedActiveId}
                guidedStepIndex={guidedStepIndex}
                guidedStepTotal={guidedStepIds.length}
                onGuidedPrev={onGuidedPrev}
                onGuidedNext={onGuidedNext}
                hideExportFooter={effectiveChromeMinimal}
              />
            </div>
          </div>
        </div>
      )}
      </div>

      {!effectiveHideControls && (
        <ConfigSummaryPanel
          open={showSummary}
          onClose={() => setShowSummary(false)}
          productId={productId}
          productName={productModel.name}
          totalPrice={totalPrice}
          activeSelections={activeSelections}
          selections={configState?.selections ?? EMPTY_SELECTIONS}
          resolveSelectionLabel={resolveSelectionLabel}
          onShare={handleShare}
          onReset={resetConfiguration}
          onDownloadSummary={handleDownloadSummary}
          shareStatus={shareStatus}
          modelUrl={assets.models[0]?.url}
          mobileRailClearancePx={mobileRailClearancePx}
        />
      )}

      {showShortcuts && (
        <HelpDialog
          onClose={() => setShowShortcuts(false)}
          modelCount={visibleModels.length}
          loadStartTime={loadStartTime}
          allLoaded={allLoaded}
          sceneStats={sceneStats}
          assetStats={diagnosticsAssetStats}
        />
      )}

      {showShareModal && shareUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setShowShareModal(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-md max-h-[85vh] overflow-y-auto bg-white dark:bg-zinc-900 rounded-xl shadow-2xl border border-zinc-200 dark:border-zinc-800 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-1">Share Configuration</h3>
            <p className="text-xs text-zinc-500 mb-4">Scan the QR code or copy the link below</p>

            <div className="flex justify-center mb-4">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(shareUrl)}`}
                alt="QR Code"
                className="w-48 h-48 rounded-lg border border-zinc-200 dark:border-zinc-700"
              />
            </div>

            <div className="flex items-center gap-2 mb-4">
              <input
                readOnly
                value={shareUrl}
                className="flex-1 text-xs bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-md px-3 py-2 text-zinc-600 dark:text-zinc-300"
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              <button
                onClick={async () => {
                  await navigator.clipboard.writeText(shareUrl);
                  setShareStatus('copied');
                  setTimeout(() => setShareStatus('idle'), 2000);
                }}
                className="shrink-0 px-3 py-2 text-xs font-medium bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 rounded-md hover:opacity-90 transition-opacity"
              >
                {shareStatus === 'copied' ? 'Copied!' : 'Copy'}
              </button>
            </div>

            {savedConfigId && (
              <div className="border-t border-zinc-200 dark:border-zinc-800 pt-4 mt-4 space-y-3">
                <div>
                  <label className="text-xs font-medium text-zinc-500 mb-1 block">Notes</label>
                  <textarea
                    value={configNotes}
                    onChange={(e) => setConfigNotes(e.target.value)}
                    onBlur={handleSaveNotes}
                    placeholder="Add notes about this configuration..."
                    rows={2}
                    className="w-full text-xs bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-md px-3 py-2 text-zinc-600 dark:text-zinc-300 resize-none"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-zinc-500 mb-1 block">
                    Annotations ({annotations.length})
                  </label>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      value={newAnnotation}
                      onChange={(e) => setNewAnnotation(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddAnnotation()}
                      placeholder="Add a comment..."
                      className="flex-1 text-xs bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-md px-3 py-2 text-zinc-600 dark:text-zinc-300"
                    />
                    <button
                      onClick={handleAddAnnotation}
                      disabled={!newAnnotation.trim()}
                      className="shrink-0 px-3 py-2 text-xs font-medium bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 rounded-md hover:opacity-90 transition-opacity disabled:opacity-40"
                    >
                      Add
                    </button>
                  </div>
                  {annotations.length > 0 && (
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {annotations.map((a) => (
                        <div key={a.id} className="flex items-start gap-2 text-xs bg-zinc-50 dark:bg-zinc-800 rounded-md px-3 py-2">
                          <span className="flex-1 text-zinc-600 dark:text-zinc-300">{a.text}</span>
                          <button
                            onClick={() => handleDeleteAnnotation(a.id)}
                            className="shrink-0 text-zinc-400 hover:text-red-500 transition-colors text-[10px]"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <button
              onClick={() => setShowShareModal(false)}
              className="w-full text-center text-xs text-zinc-400 hover:text-zinc-600 transition-colors mt-4"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {showComparison && productModel && (
        <ComparisonMode
          productModel={productModel}
          currentSelections={configState?.selections ?? EMPTY_SELECTIONS}
          resolveSelectionLabel={resolveSelectionLabel}
          onClose={() => setShowComparison(false)}
        />
      )}

      {configuratorIntroOpen && !effectiveHideControls && (
        <ConfiguratorModeIntro
          onChoose={(mode) => {
            persistExperienceMode(mode);
            setConfiguratorExperience(mode);
            setConfiguratorIntroOpen(false);
          }}
        />
      )}
    </div>
  );
}

export default ConfiguratorPlayer;
